/**
 * English strings - the default locale and the source of truth for keys.
 * `ru.ts` is typed against this file's key set (see that file's comment),
 * so a key added here without a Russian counterpart is a compile error.
 */
export const en = {
  // Nav / shell
  'nav.brand': 'Baltic Fulfilment · Ops',
  'nav.skipToContent': 'Skip to content',
  'nav.openMenu': 'Open menu',
  'nav.closeMenu': 'Close menu',
  'nav.overview': 'Overview',
  'nav.onboarding': 'Onboarding',
  'nav.queue': 'Queue',
  'nav.sellers': 'Sellers',
  'nav.inventory': 'Inventory',
  'nav.inbounds': 'Inbounds',
  'nav.orders': 'Orders',
  'nav.shipments': 'Shipments',
  'nav.returns': 'Returns',
  'nav.billing': 'Billing',
  'nav.imports': 'Imports',
  'nav.users': 'Users & Roles',
  'nav.integrations': 'Integrations',
  'nav.subscription': 'Subscription',
  'nav.problems': 'Problems',
  'nav.settings': 'Settings',
  'nav.logOut': 'Log out',
  'nav.checkingSession': 'Checking your session…',

  // Common
  'common.loading': 'Loading…',
  'common.retry': 'Retry',
  'common.save': 'Save',
  'common.saving': 'Saving…',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.create': 'Create',
  'common.edit': 'Edit',
  'common.delete': 'Delete',
  'common.confirm': 'Confirm',
  'common.back': 'Back',
  'common.next': 'Next',
  'common.previous': 'Previous',
  'common.of': 'of',
  'common.search': 'Search',
  'common.filter': 'Filter',
  'filters.aria': 'List filters',
  'filters.all': 'All',
  'filters.allSellers': 'All sellers',
  'filters.allWarehouses': 'All warehouses',
  'filters.from': 'From',
  'filters.to': 'To',
  'filters.sort': 'Sort by date',
  'filters.newest': 'Newest first',
  'filters.oldest': 'Oldest first',
  'filters.reset': 'Reset filters',
  'filters.rangeInvalid': 'The start date is after the end date: only the start date is applied.',
  'filters.created': 'Created',
  'filters.noMatch': 'Nothing matches these filters.',
  'common.status': 'Status',
  'common.actions': 'Actions',
  'common.reason': 'Reason',
  'common.note': 'Note',
  'common.required': 'Required',
  'common.optional': 'Optional',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.none': 'None',
  'common.unknown': 'Unknown',
  'common.total': 'Total',
  'common.recorded': 'Recorded',
  'common.nothingHereYet': 'Nothing here yet',
  'common.hide': 'Hide',
  'common.noAccess': "You don't have access to this",

  // Generic errors
  'error.unexpected': 'Something unexpected happened. Try again, or come back later.',
  'error.roleLastAdmin': 'This organisation would be left with no ADMIN. Assign another ADMIN first, then change this one.',
  'error.roleUnknown': 'That role does not exist.',
  'error.forbidden': 'You do not have permission to do this.',
  'error.notFound': 'That record could not be found — it may have been removed.',
  'error.sessionExpired': 'Your session is no longer valid. Log in again.',
  'error.tooManyRequests': 'Too many requests — wait a moment and try again.',
  'error.serverUnexpected': 'The server hit an unexpected error.',
  'error.requestFailed': 'The request could not be completed.',

  // Status / domain-state labels (StatusBadge)
  'status.DRAFT': 'Draft',
  'status.BUILDING': 'Building',
  'status.PICKING': 'Picking',
  'status.PICKED': 'Picked',
  'status.PACKING': 'Packing',
  'status.PACKED': 'Packed',
  'status.READY': 'Ready to ship',
  'status.SHIPPED': 'Shipped',
  'status.CANCELLED': 'Cancelled',
  'status.CANCELLED_AFTER_PICK': 'Cancelled (after pick)',
  'status.NEW': 'New',
  'status.RESERVED': 'Reserved',
  'status.ARRIVED': 'Arrived',
  'status.RECEIVING': 'Receiving',
  'status.RECEIVED': 'Received',
  'status.CLOSED': 'Closed',
  'status.ACTIVE': 'Active',
  'status.SUSPENDED': 'Suspended',
  'status.REVOKED': 'Revoked',
  'status.DISABLED': 'Disabled',
  'status.UNDECIDED': 'Undecided',
  'status.NONE': 'None',
  'status.SHORT': 'Short',
  'status.OVER': 'Over',
  'status.DAMAGED': 'Damaged',
  'status.MISMATCH': 'Mismatch',
  'status.RESTOCK': 'Restocked',
  'status.QUARANTINE': 'Quarantined',
  'status.WRITE_OFF': 'Written off',
  'status.AWAITING_DISPOSITION': 'Awaiting disposition',
  'status.ON_HOLD': 'On hold',
  'status.TERMINATED': 'Terminated',
  'status.RECEIVING_DISCREPANCY': 'Receiving discrepancy',
  'status.RETURN_AWAITING_DISPOSITION': 'Return awaiting disposition',
  'status.IMPORT_CONFLICT': 'Import conflict',
  'status.BILLING_UNPRICED': 'Unpriced billing event',
  'status.SHIPMENT_INCOMPLETE': 'Incomplete shipment',
  'status.NO_TARIFF_RULE': 'No tariff rule',
  'status.normal': 'Deadline: on track',
  'status.approaching_cutoff': 'Deadline: approaching',
  'status.urgent': 'Deadline: urgent',
  'status.blocked': 'Blocked',
  'status.blockedProblem': 'Problem to resolve',
  'status.overdue': 'Deadline: overdue',
  'status.SHORT_PICK': 'Short pick',
  'status.MISSING_MARKING': 'Missing marking',
  'status.MISSING_LABEL': 'Missing label',
  'status.INCOMPLETE_PACKAGE': 'Incomplete package',
  'status.CANCELLATION': 'Cancelled after pick',
  'status.DEADLINE_CHANGED': 'Deadline changed',
  'status.NOT_STARTED': 'Not started',
  'status.IN_PROGRESS': 'In progress',
  'status.BLOCKED': 'Blocked',
  'status.READY_FOR_REVIEW': 'Ready for review',
  'status.RECEIPT': 'Receipt',
  'status.PICK': 'Pick',
  'status.PACK': 'Pack',
  'status.PUTAWAY': 'Putaway',
  'status.SHIP': 'Ship',
  'status.MOVE': 'Move',
  'status.RETURN_RECEIPT': 'Return receipt',
  'status.CORRECTION': 'Correction',
  'status.SUPPLY': 'Supply',
  'status.RETURN': 'Return',
  'status.TRANSFER_IN': 'Transfer in',
  'status.FOUND': 'Found',
  'status.WAREHOUSE': 'Warehouse',
  'status.OWNER': 'Seller',
  'status.CARRIER': 'Carrier',
  'status.CHANNEL': 'Channel',
  'status.DAMAGE': 'Damage',
  'status.UNKNOWN_ITEM': 'Unknown item',
  'status.RETURN_INSPECTION': 'Return inspection',
  'status.RECEIVING_UNIT': 'Receiving',
  'status.RECEIVING_DOC': 'Receiving document',
  'status.PICKING_UNIT': 'Picking',
  'status.PACKING_UNIT': 'Packing',
  'status.PACKAGE_MATERIAL': 'Package material',
  'status.PALLET_HANDLING': 'Pallet handling',
  'status.STORAGE_LOCATION': 'Storage',
  'status.RETURN_UNIT': 'Return',
  'status.MARKING_SCAN': 'Marking scan',
  'status.RELABEL': 'Relabel',
  'status.DISPOSAL': 'Disposal',
  'status.MANUAL_HANDLING': 'Manual handling',
  'status.PENDING': 'Pending',
  'status.REQUESTED': 'Requested',
  'status.PRINTING': 'Printing',
  'status.PRINTED': 'Printed',
  'status.FAILED': 'Failed',
  'status.EXPECTED': 'Expected',
  'status.ASSIGNED': 'Assigned',
  'status.DONE': 'Done',
  'status.DEPARTED': 'Departed',
  'status.ACCEPTED': 'Accepted',
  'status.REJECTED': 'Rejected',
  'status.OPEN': 'Open',
  'status.ISSUED': 'Issued',
  'status.PAID': 'Paid',
  'status.PARTIALLY_PAID': 'Partially paid',
  'status.OVERDUE': 'Overdue',
  'status.SENT': 'Sent',
  'status.DISPUTED': 'Disputed',
  'status.VOID': 'Void',

  // Overview
  'overview.title': 'Overview',
  'overview.subtitle': 'What needs attention right now',
  'overview.openInbounds': 'Open inbounds',
  'overview.ordersToPick': 'Orders to pick',
  'overview.ordersToPack': 'Orders to pack',
  'overview.readyToShip': 'Ready to ship',
  'overview.returnsAwaitingDisposition': 'Returns awaiting disposition',
  'overview.importConflicts': 'Import conflicts',
  'overview.billingExceptions': 'Billing exceptions',
  'overview.problemOperations': 'Problem operations',
  'overview.needsAttention': 'Needs attention',
  'overview.nothingNeedsAttention': 'Nothing needs attention right now',
  'overview.colType': 'Type',
  'overview.colEntity': 'Entity',
  'overview.colSeller': 'Seller',
  'overview.colSeverity': 'Severity',
  'overview.colAge': 'Age',
  'overview.ageDays': '{n}d',
  'overview.ageHours': '{n}h',
  'overview.ageMinutes': '{n}m',

  // Queue
  'queue.title': 'Queue',
  'queue.sectionsAriaLabel': 'Queue sections',
  'queue.toPick': 'To pick',
  'queue.toPack': 'To pack',
  'queue.urgent': 'Urgent',
  'queue.problems': 'Problems',
  'queue.emptyTitle': 'Nothing here',
  'queue.emptyHint': 'No orders currently in this section.',
  'queue.colOrder': 'Order',
  'queue.colState': 'State',
  'queue.colChannel': 'Channel',
  'queue.colPriority': 'Deadline status',
  'queue.colDeadline': 'Deadline',
  'queue.colBlockers': 'Blockers',
  'queue.channelManual': 'Manual',
  'queue.deadlineEstimated': '{deadline} (est.)',

  // Inventory
  'inventory.title': 'Inventory',
  'inventory.searchPlaceholder': 'Search SKU code or name…',
  'inventory.searchAriaLabel': 'Search SKU code or name',
  'inventory.emptyTitle': 'No stock balances match these filters',
  'inventory.colSku': 'SKU',
  'inventory.colSeller': 'Seller',
  'inventory.colLocation': 'Location',
  'inventory.colOnHand': 'On hand',
  'inventory.colReserved': 'Reserved',
  'inventory.colAvailable': 'Available',
  'inventory.colLastMovement': 'Last movement',
  'inventory.colBucket': 'Stock status',
  'inventory.colPhysical': 'Physically in warehouse',
  'inventory.bucket.BIN': 'In bin',
  'inventory.bucket.QUARANTINE': 'Quarantine',
  'inventory.bucket.IN_PROCESS': 'In process (receiving/staging/packing)',
  'inventory.bucket.SHIPPED': 'Shipped (left custody)',
  'inventory.summary.title': 'Stock summary',
  'inventory.summary.inBins': 'Physically in bins',
  'inventory.summary.available': 'Available (pickable minus reserved)',
  'inventory.summary.quarantine': 'Quarantine',
  'inventory.summary.inProcess': 'In process (receiving/staging/packing)',
  'inventory.summary.shipped': 'Shipped (not in stock)',

  // Inventory detail
  'inventoryDetail.serialTracked': 'Serial/КИЗ tracked',
  'inventoryDetail.lotTracked': 'Lot tracked',
  'inventoryDetail.balancesByLocation': 'Balances by location',
  'inventoryDetail.noBalances': 'No balances.',
  'inventoryDetail.colWarehouse': 'Warehouse',
  'inventoryDetail.colLocation': 'Location',
  'inventoryDetail.openReservations': 'Open reservations',
  'inventoryDetail.noReservations': 'No open reservations.',
  'inventoryDetail.colQty': 'Qty',
  'inventoryDetail.colCreated': 'Created',
  'inventoryDetail.colExpires': 'Expires',
  'inventoryDetail.movementHistory': 'Movement history (read-only)',
  'inventoryDetail.noMovements': 'No recorded movements.',
  'inventoryDetail.colReason': 'Reason',
  'inventoryDetail.colDocument': 'Document',
  'inventoryDetail.colOccurred': 'Occurred',

  // Settings
  'settings.title': 'Settings',
  'settings.yourSession': 'Your session',
  'settings.role': 'Role',
  'settings.warehouse': 'Warehouse',
  'settings.orgWide': 'Org-wide',
  'settings.notSignedIn': 'Not signed in.',

  // Office roles
  'role.ADMIN': 'Admin',
  'role.WAREHOUSE': 'Warehouse',
  'role.PICKER': 'Picker',
  'role.PACKER': 'Packer',
  'role.BILLING': 'Billing',
  'role.SUPPORT': 'Support',

  // Inbounds list
  'inbounds.colNumber': 'Inbound #',
  'inbounds.colSeller': 'Seller',
  'inbounds.colWarehouse': 'Warehouse',
  'inbounds.colExpected': 'Expected',
  'inbounds.colReceived': 'Received',
  'inbounds.colDiscrepancies': 'Discrepancies',
  'inbounds.filterByStatus': 'Filter by status',
  'inbounds.allStatuses': 'All statuses',
  'inbounds.discrepanciesOnly': 'Discrepancies only',
  'inbounds.emptyTitle': 'No inbounds match these filters',

  // Inbound detail
  'inboundDetail.title': 'Inbound {number}',
  'inboundDetail.kind': 'Kind',
  'inboundDetail.source': 'Created by',
  'inboundDetail.sourceSellerPortal': 'Seller request (Seller Portal)',
  'inboundDetail.arrived': 'Arrived',
  'inboundDetail.lines': 'Lines',
  'inboundDetail.noLines': 'No lines recorded.',
  'inboundDetail.discrepancy': 'Discrepancy',
  'inboundDetail.responsibility': 'Responsibility',
  'inboundDetail.evidence': 'Evidence',
  'inboundDetail.photos': 'Photos',
  'inboundDetail.dispositionHistory': 'Disposition / evidence history',
  'inboundDetail.noDispositions': 'No dispositions recorded for this inbound.',
  'inboundDetail.colQty': 'Qty',
  'inboundDetail.colDisposition': 'Disposition',
  'inboundDetail.colToLocation': 'To location',
  'inboundDetail.colRecorded': 'Recorded',
  'inboundDetail.colReversed': 'Reversed',

  // Orders list
  'orders.colNumber': 'Order #',
  'orders.colReserved': 'Reserved',
  'orders.colPicked': 'Picked',
  'orders.colPacked': 'Packed',
  'orders.colDue': 'Due',
  'orders.emptyTitle': 'No orders match these filters',

  // Order detail
  'orderDetail.title': 'Order {number}',
  'orderDetail.noLines': 'No lines.',
  'orderDetail.colOrdered': 'Ordered',
  'orderDetail.colShipped': 'Shipped',
  'orderDetail.activityTimeline': 'Activity timeline',
  'orderDetail.noActivity': 'No activity recorded.',
  'orderDetail.byActor': 'by {actor}',

  // Shipments list
  'shipments.colNumber': 'Shipment #',
  'shipments.colPackages': 'Packages',
  'shipments.colCarrier': 'Carrier',
  'shipments.colShippedAt': 'Shipped at',
  'shipments.notReadyOnly': 'Not ready / incomplete only',
  'shipments.emptyTitle': 'No shipments match these filters',

  // Shipment detail
  'shipmentDetail.title': 'Shipment {number}',
  'shipmentDetail.carrierRef': 'Carrier ref',
  'shipmentDetail.departed': 'Departed',
  'shipmentDetail.notYetDeparted': 'Not yet departed',
  'shipmentDetail.packagesCount': 'Packages ({n})',
  'shipmentDetail.noPackages': 'No packages built yet.',
  'shipmentDetail.colCode': 'Code',
  'shipmentDetail.colWeight': 'Weight (g)',
  'shipmentDetail.colContents': 'Contents',
  'shipmentDetail.colLabel': 'Label',
  'shipmentDetail.custodyExit': 'Custody exit (SHIP movement)',
  'shipmentDetail.custodyNotExited': 'Custody has not exited yet — this shipment has not shipped.',
  'shipmentDetail.requestLabel': 'Request transport label',
  'shipmentDetail.printPackageLabel': 'Print package label',
  'shipmentDetail.printPackageLabelA4': 'PDF A4 (plain printer)',
  'shipmentDetail.noTransportLabel': 'No marketplace transport label is needed: the order did not come through a marketplace connection. Print the package label.',
  'shipmentDetail.print': 'Print',
  'shipmentDetail.retryPrint': 'Retry print',
  'shipmentDetail.lastError': 'Last error: {code}',
  'shipmentDetail.requestFailed': 'Request failed',
  'shipmentDetail.printFailed': 'Print failed',

  // Returns list
  'returns.colNumber': 'Return #',
  'returns.colDispositioned': 'Dispositioned',
  'returns.colUnresolved': 'Unresolved',
  'returns.colReceivedAt': 'Received at',
  'returns.awaitingDisposition': 'Awaiting disposition',
  'returns.filterByDisposition': 'Filter by disposition',
  'returns.allDispositions': 'All dispositions',
  'returns.emptyTitle': 'No returns match these filters',

  // Return detail
  'returnDetail.title': 'Return {number}',
  'returnDetail.whatCameBack': 'What came back',
  'returnDetail.colQtyReceived': 'Qty received',
  'returnDetail.colCondition': 'Condition',
  'returnDetail.dispositionDecisions': 'Disposition decisions',
  'returnDetail.awaitingDisposition': 'Awaiting disposition — no decision has been recorded yet.',
  'returnDetail.colDecidedBy': 'Decided by',
  'returnDetail.colTimestamp': 'Timestamp',
  'returnDetail.colPostLocation': 'Post-disposition location',

  // Login
  'login.title': 'Sign in to WMS Office',
  'login.subtitle':
    'Paste the office bearer token issued for your membership. An administrator issues this from Users & Roles, or an operator provisions the first one with the office bootstrap CLI.',
  'login.bearerToken': 'Bearer token',
  'login.signIn': 'Sign in',
  'login.signingIn': 'Signing in…',
  'login.tokenInvalid': 'That token is invalid, expired, or revoked.',
  'login.couldNotSignIn': 'Could not sign in. Try again.',

  // Billing
  'billing.tabOverview': 'Overview',
  'billing.tabContracts': 'Contracts',
  'billing.tabTariffs': 'Tariffs',
  'billing.tabCharges': 'Charges',
  'billing.tabInvoices': 'Invoices',
  'billing.tabManualService': 'New manual service',
  'billing.tabLegalEntitySettings': 'Legal Entity Settings',
  'billing.tabDataExport': 'Data Export',
  'billing.permissionDenied': 'Billing is visible to ADMIN, BILLING and SUPPORT (read-only for SUPPORT).',
  'billing.manualServicePermissionDenied': 'Only ADMIN/BILLING may record a manual service.',
  'billing.dataExportPermissionDenied': 'Tenant data export is ADMIN-only.',
  'billing.sellerServiceBreakdowns': 'Seller/service breakdowns this period',
  'billing.unpricedBillableEvents': 'Unpriced billable events',
  'billing.operationsWithNoCharge': 'Operations with no charge',
  'billing.everyOperationCharged': 'Every physical operation has produced a charge',
  'billing.noContractsYet': 'No contracts yet',
  'billing.tariff': 'Tariff',
  'billing.currency': 'Currency',
  'billing.effectiveFrom': 'Effective from',
  'billing.colCode': 'Code',
  'billing.colKind': 'Kind',
  'billing.colRate': 'Rate',
  'billing.colUnit': 'Unit',
  'billing.priceAService': 'Price a service',
  'billing.priceAServiceHint': 'Never an in-place update — this closes the current rule and opens a new one, effective now.',
  'billing.serviceCode': 'Service code',
  'billing.saveNewRate': 'Save new rate',
  'billing.noChargesYet': 'No charges recorded yet',
  'billing.colDate': 'Date',
  'billing.colAmount': 'Amount',
  'billing.colSource': 'Source',
  'billing.manual': 'Manual',
  'billing.hideEvidence': 'Hide evidence',
  'billing.chargeEvidence': 'Charge evidence',
  'billing.physicalOperation': 'Physical operation',
  'billing.noneManualEntry': 'None (manual entry)',
  'billing.businessDocument': 'Business document',
  'billing.actorDevice': 'Actor / device',
  'billing.tariffRuleSnapshot': 'Tariff rule snapshot',
  'billing.noTariffRuleApplied': 'No tariff rule applied',
  'billing.calculation': 'Calculation',
  'billing.reasonNote': 'Reason / note',
  'billing.newManualService': 'New manual billable service',
  'billing.newManualServiceHint': 'Reason and evidence are mandatory — this is a real, audited charge, never a bare total-amount field.',
  'billing.sellerOwnerId': 'Seller',
  'billing.selectSeller': 'Select a seller',
  'billing.quantity': 'Quantity',
  'billing.rateInfoOnly': 'Rate (informational only — tariff rules still apply)',
  'billing.reasonRequired': 'Reason (required)',
  'billing.referenceDocKind': 'Reference document type (optional)',
  'billing.recording': 'Recording…',
  'billing.recordCharge': 'Record charge',
  'billing.allSellers': 'All sellers',
  'billing.statusIssued': 'Issued',
  'billing.statusPartiallyPaid': 'Partially paid',
  'billing.statusPaid': 'Paid',
  'billing.statusVoid': 'Void',
  'billing.noInvoicesMatch': 'No invoices match these filters',
  'billing.colNumber': 'Number',
  'billing.colPeriod': 'Period',
  'billing.generateDraftInvoice': 'Generate a draft invoice',
  'billing.generateDraftInvoiceHint':
    'Claims every unbilled charge for this contract in the given period. Calling this again on the same still-open draft is safe - it only adds newly-unbilled charges, it never duplicates or rewrites existing lines.',
  'billing.contract': 'Contract',
  'billing.selectContract': 'Select a contract…',
  'billing.periodStart': 'Period start',
  'billing.periodEnd': 'Period end',
  'billing.generating': 'Generating…',
  'billing.generateRegenerateDraft': 'Generate / regenerate draft',
  'billing.subtotal': 'Subtotal',
  'billing.tax': 'Tax',
  'billing.paid': 'Paid',
  'billing.outstandingBalance': 'Outstanding balance',
  'billing.linesTraceable': 'Lines - each traceable to its source charge',
  'billing.description': 'Description',
  'billing.sourceCharge': 'Source charge',
  'billing.downloadPdf': 'Download PDF',
  'billing.issue': 'Issue',
  'billing.voidReason': 'Void reason',
  'billing.void': 'Void',
  'billing.recordPaymentLabel': 'Record payment (manual reconciliation - no payment gateway)',
  'billing.amountInMinorUnits': 'Amount in minor units',
  'billing.recordPayment': 'Record payment',
  'billing.adjustmentLabel': 'Adjustment (credit/debit note - never rewrites the issued line snapshot)',
  'billing.creditNote': 'Credit note',
  'billing.debitNote': 'Debit note',
  'billing.reasonRequiredPlaceholder': 'Reason (required)',
  'billing.apply': 'Apply',
  'billing.legalEntity': 'Legal entity',
  'billing.selectLegalEntity': 'Select a legal entity…',
  'billing.noLegalEntitiesYet': 'No legal entities yet — create one in Onboarding first.',
  'billing.taxVatConfig': 'Tax / VAT configuration',
  'billing.taxVatConfigHint':
    'Never invented: a legal entity with no configuration here charges no tax on issued invoices, distinctly from an explicit "exempt" entry - both are visible below, never confused with each other.',
  'billing.currentlyActive': 'Currently active',
  'billing.exempt': 'Exempt',
  'billing.noTaxConfigOnFile': 'No tax configuration on file - invoices for this legal entity carry zero tax until one is added.',
  'billing.jurisdiction': 'Jurisdiction',
  'billing.active': 'Active',
  'billing.addNewVersion': 'Add a new version',
  'billing.jurisdictionIso': 'Jurisdiction (ISO country code)',
  'billing.exemptNoVat': 'Exempt (no VAT)',
  'billing.rateFraction': 'Rate (fraction, e.g. 0.19 for 19%)',
  'billing.taxVatRegNumber': 'Tax/VAT registration number',
  'billing.label': 'Label',
  'billing.saveNewTaxConfig': 'Save new tax configuration',
  'billing.invoiceLegalTemplate': 'Invoice legal template',
  'billing.invoiceLegalTemplateHint': 'Snapshotted into every invoice at the moment it is issued - editing this here never changes an already-issued invoice.',
  'billing.sellerLegalName': 'Seller legal name',
  'billing.registrationNumber': 'Registration number',
  'billing.taxVatId': 'Tax/VAT ID',
  'billing.noInvoiceTemplateOnFile': 'No invoice template on file - issued invoices render the generic fallback artefact.',
  'billing.stillMissing': 'Still missing before this template is complete: {fields}.',
  'billing.saveNewVersion': 'Save a new version',
  'billing.footerText': 'Footer text',
  'billing.paymentTerms': 'Payment terms',
  'billing.pdfLanguage': 'Invoice PDF language',
  'billing.pdfLanguageEn': 'English (default)',
  'billing.pdfLanguageRu': 'Russian — payment invoice',
  'billing.legalForm': 'Legal form',
  'billing.legalFormIp': 'Individual entrepreneur (IP)',
  'billing.legalFormOoo': 'Company (OOO)',
  'billing.kpp': 'KPP (companies only)',
  'billing.ruAddress': 'Registered address',
  'billing.bankName': 'Bank',
  'billing.bik': 'BIK',
  'billing.settlementAccount': 'Settlement account',
  'billing.correspondentAccount': 'Correspondent account',
  'billing.signerPosition': 'Signatory position',
  'billing.signerName': 'Signatory name',
  'billing.paymentTermsPlaceholder': 'e.g. Net 14',
  'billing.saveNewTemplateVersion': 'Save new template version',
  'billing.fullOrgExport': 'Full organisation export',
  'billing.fullOrgExportHint':
    "A complete, tenant-scoped JSON export of this organisation's own data - legal entities, warehouses, catalog, inventory, orders/shipments/returns, billing, membership metadata (no credentials), audit log, and an evidence manifest (storage keys, never the photo bytes themselves).",
  'billing.generateNewExport': 'Generate a new export',
  'billing.previousExports': 'Previous exports',
  'billing.noExportGenerated': 'No export has been generated yet',
  'billing.colGenerated': 'Generated',
  'billing.colSize': 'Size',
  'billing.sizeKb': '{kb} KB',
  'billing.download': 'Download',

  // Onboarding
  'onboarding.stepOrgProfileTitle': 'Organisation profile',
  'onboarding.stepOrgProfileDesc': 'Created automatically when your organisation was provisioned.',
  'onboarding.stepLegalEntityTitle': 'Legal entity',
  'onboarding.stepLegalEntityDesc': 'The invoicing entity operations run under.',
  'onboarding.stepWarehouseTitle': 'Warehouse',
  'onboarding.stepWarehouseDesc': 'At least one physical warehouse.',
  'onboarding.stepWarehouseTimezoneTitle': 'Warehouse timezone',
  'onboarding.stepWarehouseTimezoneDesc': 'Every warehouse needs an IANA timezone for cut-offs and shifts.',
  'onboarding.stepSystemLocationsTitle': 'System locations',
  'onboarding.stepSystemLocationsDesc': 'RECEIVING, PACKING and QUARANTINE locations for each warehouse.',
  'onboarding.stepFirstSellerTitle': 'First seller',
  'onboarding.stepFirstSellerDesc': 'At least one seller (owner) on file.',
  'onboarding.stepSellerContractTitle': 'Seller contract',
  'onboarding.stepSellerContractDesc': 'A contract linking a seller to a legal entity.',
  'onboarding.stepTariffAssignmentTitle': 'Tariff assignment',
  'onboarding.stepTariffAssignmentDesc': 'Only required once advanced billing is enabled for this organisation.',
  'onboarding.stepInternalUsersTitle': 'Internal users',
  'onboarding.stepInternalUsersDesc': 'At least one ADMIN membership.',
  'onboarding.stepSellerPortalUsersTitle': 'Seller portal access',
  'onboarding.stepSellerPortalUsersDesc': 'Only checked for owners who already have a portal login configured.',
  'onboarding.stepInitialImportTitle': 'Initial data import',
  'onboarding.stepInitialImportDesc': 'Optional - bulk-importing sellers/SKUs/locations/stock via CSV.',
  'onboarding.stepReconciliationTitle': 'Opening-balance reconciliation',
  'onboarding.stepReconciliationDesc': 'A committed STOCK_BALANCE import batch, or an explicit, audited skip.',
  'onboarding.notRequired': 'Not required',
  'onboarding.goFixThis': 'Go fix this',
  'onboarding.setupOrgStructure': 'Set up organisation structure',
  'onboarding.step1LegalEntity': '1. Legal entity',
  'onboarding.name': 'Name',
  'onboarding.countryIso2': 'Country (ISO-2)',
  'onboarding.currencyIso3': 'Currency (ISO-3)',
  'onboarding.createLegalEntity': 'Create legal entity',
  'onboarding.step2Warehouse': '2. Warehouse',
  'onboarding.timezoneIana': 'Timezone (IANA)',
  'onboarding.createWarehouse': 'Create warehouse',
  'onboarding.step3SystemLocations': '3. System locations (Receiving / Packing / Quarantine)',
  'onboarding.selectWarehouse': 'Select a warehouse…',
  'onboarding.createSystemLocations': 'Create missing system locations',
  'onboarding.step4FirstSeller': '4. First seller',
  'onboarding.createSeller': 'Create seller',
  'onboarding.otherStepsHint':
    "Seller contracts and tariff assignment happen on the Billing page; internal users on Users & Roles; seller portal logins on a seller's own detail page. Completing a step anywhere in the product updates the checklist above.",
  'onboarding.permissionDenied': 'Onboarding setup and readiness are visible to ADMIN only.',
  'onboarding.stepsComplete': '{done} of {total} required steps complete.',
  'onboarding.colStep': 'Step',
  'onboarding.skipReconciliation': 'Skip opening-balance reconciliation',
  'onboarding.skipReconciliationHint':
    'Only do this if opening balances were entered another way (or genuinely do not exist yet). This is audited and requires a reason.',
  'onboarding.skipWithReason': 'Skip with reason',
  'onboarding.reconciliationSkippedNote': 'Reconciliation was explicitly skipped on {at}: "{reason}".',
  'onboarding.activation': 'Activation',
  'onboarding.activatedOn': 'This organisation was activated on {at}.',
  'onboarding.everyCheckPasses': 'Every required check passes. Activating is a one-way, audited action.',
  'onboarding.blockersRemain': '{n} blocker(s) remain - activation is disabled until every one clears.',
  'onboarding.activateOrganisation': 'Activate organisation',

  // Users & Roles
  'users.rolesNote': 'BILLING cannot touch warehouse operations, SUPPORT cannot touch tariffs, and the last ADMIN in an organisation cannot be demoted or suspended.',
  'users.noMembersYet': 'No members yet',
  'users.email': 'Email',
  'users.roleForAriaLabel': 'Role for {name}',
  'users.cannotChangeOwnRole': 'You cannot change your own role (no self-escalation).',
  'users.suspend': 'Suspend',
  'users.reactivate': 'Reactivate',

  // Channels / Integrations
  'channels.credentialsHint': 'Credentials are encrypted at rest and never shown here beyond a recognisability hint (e.g. "****1234").',
  'channels.emptyTitle': 'No marketplace channels connected yet',
  'channels.colMarketplace': 'Marketplace',
  'channels.colConnection': 'Connection',
  'channels.colCredential': 'Credential',
  'channels.colSyncStatus': 'Sync status',
  'channels.colLastSuccess': 'Last success',
  'channels.colLastError': 'Last error',
  'channels.colNextSync': 'Next sync',
  'channels.notConfigured': 'Not configured',
  'channels.syncNow': 'Sync now',
  'channels.credential': 'Credential',
  'channels.syncDetail': 'Sync detail',
  'channels.noSyncStateYet': 'No sync state yet — write a credential to start syncing.',
  'channels.consecutiveFailures': 'Consecutive failures',
  'channels.lastPolled': 'Last polled',
  'channels.configured': 'Configured',
  'channels.yesWithHint': 'Yes ({hint})',
  'channels.revoked': 'Revoked',
  'channels.lastRotated': 'Last rotated',
  'channels.never': 'Never',
  'channels.noCredentialSlot': 'No credential slot found.',
  'channels.roleCannotWrite': 'Your role cannot write credentials.',
  'channels.rotateCredential': 'Rotate credential (new value replaces the old one)',
  'channels.setCredential': 'Set credential',
  'channels.pasteApiToken': 'Paste the marketplace API token',
  'channels.rotate': 'Rotate',
  'channels.revoke': 'Revoke',
  'channels.encryptedHint': 'The value you paste is encrypted immediately and never shown again — not here, not in any API response.',

  // Sellers
  'sellers.active': 'Active',
  'sellers.inactive': 'Inactive',
  'sellers.stockSkus': 'Stock SKUs',
  'sellers.openOrders': 'Open orders',
  'sellers.lastActivity': 'Last activity',
  'sellers.activeOnly': 'Active only',
  'sellers.emptyTitle': 'No sellers found',

  // Seller detail
  'sellerDetail.tabStock': 'Stock',
  'sellerDetail.tabPortalUsers': 'Portal Users',
  'sellerDetail.tabActivity': 'Activity',
  'sellerDetail.contracts': 'Contracts',
  'sellerDetail.openOrders': 'Open orders',
  'sellerDetail.openInbounds': 'Open inbounds',
  'sellerDetail.noStock': 'No stock recorded for this seller.',
  'sellerDetail.skus': 'SKUs',
  'sellerDetail.noOpenInbounds': 'No open inbounds.',
  'sellerDetail.number': 'Number',
  'sellerDetail.noOpenOrders': 'No open orders.',
  'sellerDetail.seeReturnsSection': "See the Returns section, filtered to this seller's name, for full return detail.",
  'sellerDetail.noContractsOnFile': 'No contracts on file.',
  'sellerDetail.validFrom': 'Valid from',
  'sellerDetail.activityNotImplemented': 'Per-seller activity timeline is not implemented separately — see Orders/Inbounds activity tabs.',

  // Portal users panel
  'portalUsers.title': 'Seller portal users',
  'portalUsers.inviteUser': 'Invite a portal user',
  'portalUsers.displayName': 'Display name',
  'portalUsers.sendInvite': 'Send invite',
  'portalUsers.noEmailProviderNote':
    'No transactional email provider is configured yet - after you send or resend an invite, a one-time setup link will be shown right here so you can copy it and share it with the seller yourself.',
  'portalUsers.emptyTitle': 'No portal users for this seller yet',
  'portalUsers.lastLogin': 'Last login',
  'portalUsers.activeSessions': 'Active sessions',
  'portalUsers.resendInvite': 'Resend invite',
  'portalUsers.sendPasswordReset': 'Send password reset',
  'portalUsers.disable': 'Disable',
  'portalUsers.enable': 'Enable',
  'portalUsers.revokeSessions': 'Revoke sessions',
  'portalUsers.hideAudit': 'Hide audit',
  'portalUsers.viewAudit': 'View audit',
  'portalUsers.noAuditEvents': 'No audit events yet',
  'portalUsers.setupLinkFor': 'One-time setup link for {email}',
  'portalUsers.setupLinkWarning': 'Copy this link now. It will not be shown again.',
  'portalUsers.setupLinkCopy': 'Copy link',
  'portalUsers.setupLinkCopied': 'Copied',

  // Evidence gallery
  'evidence.onlyImagesAccepted': 'Only JPEG, PNG, or WEBP photos are accepted.',
  'evidence.uploadFailed': 'Upload failed. Try again.',
  'evidence.noPermission': "You don't have permission to view evidence for this record.",
  'evidence.loading': 'Loading evidence…',
  'evidence.couldNotLoad': 'Evidence could not be loaded.',
  'evidence.noneUploaded': 'No evidence uploaded yet.',
  'evidence.view': 'View',
  'evidence.altText': '{kind} evidence',
  'evidence.uploading': 'Uploading…',

  // Problems
  'problems.title': 'Problems / Exceptions',
  'problems.hasDiscrepancy': 'Has discrepancy',
  'problems.incomplete': 'Incomplete',
  'problems.unpriced': 'Unpriced',
  'problems.awaitingDisposition': 'Awaiting disposition',
  'problems.emptyTitle': 'No open problems',
  'problems.emptyHint': 'Nothing matches these filters right now.',

  // Subscription
  'subscription.metricWarehouses': 'Warehouses',
  'subscription.metricInternalUsers': 'Internal users',
  'subscription.metricSellerPortalUsers': 'Seller portal logins',
  'subscription.metricMarketplaceConnections': 'Marketplace connections',
  'subscription.metricSellers': 'Sellers',
  'subscription.metricEvidenceStorage': 'Evidence storage',
  'subscription.metricEvidenceRetention': 'Evidence retention',
  'subscription.capFbs': 'Fulfilment by Seller',
  'subscription.capFbo': 'Fulfilment by Operator',
  'subscription.capReturns': 'Returns processing',
  'subscription.capMultiWarehouse': 'Multiple warehouses',
  'subscription.capMultiLegalEntity': 'Multiple legal entities',
  'subscription.capAdvancedBilling': 'Advanced billing',
  'subscription.capSellerPortal': 'Seller portal',
  'subscription.capPublicApi': 'Public API access',
  'subscription.capWebhooks': 'Webhooks',
  'subscription.capDataExport': 'Data export',
  'subscription.capMigrationWizard': 'Migration wizard',
  'subscription.capChestnyZnak': 'Chestny Znak marking',
  'subscription.capEdo': 'Electronic document exchange',
  'subscription.capAdvancedReports': 'Advanced reports (coming soon)',
  'subscription.capVideoEvidence': 'Video evidence (coming soon)',
  'subscription.unlimited': 'Unlimited',
  'subscription.nDays': '{n} days',
  'subscription.noSubscriptionOnFile': 'No subscription on file',
  'subscription.noSubscriptionHint': 'This organisation has not been assigned a plan, so nothing is limited. Contact your platform administrator to put it on a plan.',
  'subscription.readOnlyTitle': 'Your subscription is read-only',
  'subscription.reasonPrefix': 'Reason: {reason}. ',
  'subscription.readOnlyHint':
    'All of your data is intact and remains fully visible — warehouses, stock, orders and history are untouched. While the subscription is {status}, new warehouses, users, seller logins and marketplace connections cannot be created. Contact your platform administrator to restore it.',
  'subscription.pastDueBadge': 'PAST DUE',
  'subscription.paymentOutstanding': 'Payment outstanding',
  'subscription.pastDueHint':
    'Everything still works in full until {date}. After that the subscription becomes read-only — nothing is deleted, but new warehouses, users and connections will be blocked.',
  'subscription.trialBadge': 'TRIAL',
  'subscription.trialEnds': 'Trial ends {date}',
  'subscription.trialHint': 'Full access to everything in the {plan} plan.',
  'subscription.currentPlanFallback': 'current',
  'subscription.cancelledBadge': 'CANCELLED',
  'subscription.ends': 'Ends {date}',
  'subscription.cancelledHint': 'You keep full access until the end of the period you have already paid for. Your data is kept after that; the subscription simply becomes read-only.',
  'subscription.activeBadge': 'ACTIVE',
  'subscription.renews': 'Renews {date}.',
  'subscription.usageAndLimits': 'Usage and limits',
  'subscription.colResource': 'Resource',
  'subscription.colInUse': 'In use',
  'subscription.colIncluded': 'Included',
  'subscription.adjusted': 'Adjusted',
  'subscription.notALimit': 'Not a limit',
  'subscription.limitReached': 'Limit reached',
  'subscription.nLeft': '{n} left',
  'subscription.includedFeatures': 'Included features',
  'subscription.noFeaturesListed': 'No features are listed for this organisation.',
  'subscription.currentlyBlocked': 'Currently blocked',
  'subscription.changePlan': 'Change plan',
  'subscription.scheduledChangeText': '{plan} ({kind}) is scheduled for {date}.',
  'subscription.changeKind.UPGRADE': 'upgrade',
  'subscription.changeKind.DOWNGRADE': 'downgrade',
  'subscription.interval.MONTH': 'month',
  'subscription.interval.YEAR': 'year',
  'subscription.waitingForApproval': 'Waiting for your platform administrator to approve it. Upgrades take effect only once approved.',
  'subscription.withdrawChange': 'Withdraw this change',
  'subscription.scheduledChangeWithdrawn': 'The scheduled plan change has been withdrawn.',
  'subscription.upgradeRequestedNotice': 'Upgrade requested. It takes effect on {date} once your platform administrator approves it.',
  'subscription.changeScheduledNotice': 'Change scheduled for {date}. Nothing changes before then, and nothing you already have is removed.',
  'subscription.moveTo': 'Move to',
  'subscription.selectPlan': 'Select a plan…',
  'subscription.requestChange': 'Request change',
  'subscription.requestChangeHint': "Changes take effect at the end of the current period. Upgrades need your platform administrator's approval; reducing your plan does not.",
  'subscription.cancellationWithdrawn': 'Your cancellation has been withdrawn.',
  'subscription.keepMySubscription': 'Keep my subscription',
  'subscription.cancelledNotice': 'Cancelled. You keep full access until the end of the current period, and your data is kept.',
  'subscription.cancelAtPeriodEnd': 'Cancel at period end',
  'subscription.permissionDenied': 'Only administrators and billing users can see the subscription.',
  'subscription.loading': 'Loading your subscription…',
  'subscription.planSummary': '{price} per {interval} · version {version} · current period {from} – {to}',
  'subscription.history': 'History',
  'subscription.colWhen': 'When',
  'subscription.colChange': 'Change',
  'subscription.colBy': 'By',

  // Imports list
  'imports.newImport': 'New import',
  'imports.emptyTitle': 'No imports yet',
  'imports.emptyHint': 'Start a new import to bring in sellers, SKUs, or opening stock.',
  'imports.colFile': 'File',
  'imports.colType': 'Type',
  'imports.colRows': 'Rows',
  'imports.colCreated': 'Created',

  // Import wizard
  'importWizard.stepUpload': 'Upload',
  'importWizard.stepMapping': 'Mapping',
  'importWizard.stepValidate': 'Validation / Conflicts',
  'importWizard.stepDryRun': 'Dry Run',
  'importWizard.stepCommit': 'Commit',
  'importWizard.stepSummary': 'Summary',
  'importWizard.title': 'Import wizard',
  'importWizard.whatAreYouImporting': 'What are you importing?',
  'importWizard.csvFile': 'CSV file',
  'importWizard.uploading': 'Uploading…',
  'importWizard.uploadAndParse': 'Upload & parse',
  'importWizard.mappingHint': 'Map each source column to a canonical field. Suggested mappings are pre-filled from past confirmations.',
  'importWizard.colSourceColumn': 'Source column',
  'importWizard.colSample': 'Sample',
  'importWizard.colCanonicalField': 'Canonical field',
  'importWizard.suggested': 'suggested',
  'importWizard.unmapped': 'unmapped',
  'importWizard.canonicalFieldFor': 'Canonical field for {column}',
  'importWizard.applying': 'Applying…',
  'importWizard.applyMappingAndValidate': 'Apply mapping & validate',
  'importWizard.validRows': 'Valid rows',
  'importWizard.invalidRows': 'Invalid rows',
  'importWizard.openConflicts': 'Open conflicts',
  'importWizard.conflictBannerText':
    'UPDATE_EXISTING really updates the existing record in place at commit. CREATE_NEW is only accepted for a duplicate-barcode conflict (a genuinely new SKU, without moving the barcode); for an existing seller, SKU or location it is refused rather than silently disambiguated, since guessing a new identity there could misattribute stock or billing.',
  'importWizard.noIssuesFound': 'No issues found.',
  'importWizard.colRow': 'Row',
  'importWizard.colDetails': 'Details',
  'importWizard.colResolve': 'Resolve',
  'importWizard.choose': 'Choose…',
  'importWizard.skipRow': 'Skip row',
  'importWizard.updateExisting': 'Update existing (behaves as SKIP)',
  'importWizard.createNew': 'Create new (behaves as SKIP)',
  'importWizard.markHardError': 'Mark as hard error',
  'importWizard.revalidate': 'Re-validate',
  'importWizard.continueToDryRun': 'Continue to dry run',
  'importWizard.runValidation': 'Run validation',
  'importWizard.dryRunHint': 'Nothing has been written yet. Review before committing.',
  'importWizard.rowsValid': 'Rows valid',
  'importWizard.conflicts': 'Conflicts',
  'importWizard.entitiesToCreate': 'Entities to create',
  'importWizard.openingStockMovements': 'Opening stock movements',
  'importWizard.rowsToSkip': 'Rows to skip',
  'importWizard.confirmCommitLabel': 'I have reviewed the dry run and want to commit this import.',
  'importWizard.committing': 'Committing…',
  'importWizard.commitImport': 'Commit import',
  'importWizard.alreadyCommitted': 'Already committed (no-op)',
  'importWizard.committed': 'Committed',
  'importWizard.created': 'Created',
  'importWizard.skipped': 'Skipped',
  'importWizard.failed': 'Failed',
  'importWizard.backToImports': 'Back to imports',

  // Ops create-order flow + Billing lookups (no raw ids)
  'orders.newOrder': 'New order',
  'orderCreate.title': 'New order',
  'orderCreate.subtitle': 'Operator-entered order. Stock is reserved as soon as you create it.',
  'orderCreate.permissionDenied': 'Only ADMIN and WAREHOUSE roles may create orders.',
  'orderCreate.seller': 'Seller',
  'orderCreate.selectSeller': 'Select a seller',
  'orderCreate.warehouse': 'Warehouse',
  'orderCreate.selectWarehouse': 'Select a warehouse',
  'orderCreate.chooseSellerWarehouseFirst': 'Choose a seller and a warehouse to add items.',
  'orderCreate.sellerChangeResets': 'Changing the seller or warehouse clears the order lines.',
  'orderCreate.findItem': 'Find item (code, name or barcode)',
  'orderCreate.itemResults': 'Matching items',
  'orderCreate.noItemsFound': 'No items of this seller match.',
  'orderCreate.addItem': 'Add {sku}',
  'orderCreate.availableQty': '{qty} available',
  'orderCreate.lines': 'Order lines',
  'orderCreate.noLines': 'No items yet — find an item above and add it.',
  'orderCreate.colItem': 'Item',
  'orderCreate.colAvailable': 'Available',
  'orderCreate.colQty': 'Quantity',
  'orderCreate.qtyFor': 'Quantity for {sku}',
  'orderCreate.removeLine': 'Remove {sku}',
  'orderCreate.remove': 'Remove',
  'orderCreate.lineMerged': '{sku} is already in the order — its quantity was increased instead of adding a second line.',
  'orderCreate.qtyInvalid': 'Quantity for {sku} must be a whole number greater than zero.',
  'orderCreate.qtyExceedsAvailable': 'Not enough stock for {sku}: {available} available, {requested} requested.',
  'orderCreate.orderDetails': 'Order details',
  'orderCreate.orderNumber': 'Order number (optional)',
  'orderCreate.orderNumberHint': 'Leave blank to generate one automatically.',
  'orderCreate.dueDate': 'Ship by (optional)',
  'orderCreate.recipient': 'Recipient',
  'orderCreate.recipientName': 'Recipient name',
  'orderCreate.phone': 'Phone',
  'orderCreate.address': 'Address',
  'orderCreate.city': 'City',
  'orderCreate.postalCode': 'Postal code',
  'orderCreate.country': 'Country',
  'orderCreate.submit': 'Create order and reserve stock',
  'orderCreate.submitting': 'Creating…',
  'orderCreate.fixErrors': 'Fix the highlighted lines before creating the order.',
  'orderCreate.success': 'Order {number} created — stock reserved.',
  'orderCreate.openOrder': 'Open order {number}',
  'orderCreate.createAnother': 'Create another order',
  'orderCreate.manualSource': 'Manual (operator)',
  'orderCreate.sellerPortalSource': 'Seller Portal (created by the seller)',
  'orderCreate.err.insufficientStock': 'Not enough stock for {sku}: {available} available, {requested} requested. Nothing was reserved.',
  'orderCreate.err.skuNotOwned': 'Item {sku} does not belong to the selected seller.',
  'orderCreate.err.linesRequired': 'Add at least one item.',
  'orderCreate.err.numberTaken': 'Order number {number} is already used — choose another or leave it blank.',
  'orderCreate.err.warehouseOutOfScope': 'You can only create orders for your own warehouse.',
  'orderCreate.err.sellerArchived': 'This seller is archived and cannot receive new orders.',
  'orderCreate.err.warehouseArchived': 'This warehouse is archived and cannot receive new orders.',
  'orderCreate.err.notFound': 'The seller or warehouse no longer exists. Reload the page and choose again.',
  'billing.selectTariff': 'Select a tariff to inspect its rules',
  'billing.noTariffsYet': 'No tariffs yet',
  'billing.referenceDocument': 'Reference document',
  'billing.docKindNone': 'None',
  'billing.docKindOrder': 'Sales order',
  'billing.docKindInbound': 'Inbound shipment',
  'billing.searchDocumentNumber': 'Search by document number',
  'billing.selectDocument': 'Select a document',
  'billing.selectSellerFirst': 'Select a seller first',
  'billing.noDocumentsForSeller': 'This seller has no documents of this type.',
  // ADR-083 - warehouses, zones, cells, codes
  'nav.warehouses': 'Warehouses',
  'warehouses.subtitle': 'Your warehouses, their areas and storage cells.',
  'warehouses.create': 'Create warehouse',
  'warehouses.orgAdminOnly': 'Only an administrator of the whole organisation can create a warehouse. Your access is limited to one warehouse, so you can set up its areas and cells but not add new warehouses.',
  'warehouses.createdTitle': 'Warehouse “{name}” is created',
  'warehouses.createdBody': 'Code {code}. The places every operation needs (receiving, picking, packing, quarantine, shipped goods) were created automatically. Next, add storage areas and cells.',
  'warehouses.nextSetupZones': 'Set up zones and storage places →',
  'warehouses.empty': 'No warehouses yet.',
  'warehouses.zonesCount': 'Zones: {n}',
  'warehouses.cellsCount': 'Cells: {n}',
  'warehouses.ready': 'Ready for operations',
  'warehouses.notReady': 'System places missing',
  'warehouses.createTitle': 'New warehouse',
  'warehouses.noLegalEntity': 'To create a warehouse you first need a legal entity. Create it in',
  'warehouses.name': 'Warehouse name',
  'warehouses.namePlaceholder': 'e.g. Riga fulfilment centre 2',
  'warehouses.timeZone': 'Time zone',
  'warehouses.timeZoneHint': 'Used for dates, cut-off times and storage-day billing.',
  'warehouses.systemLocationsNote': 'Receiving, picking, packing, quarantine and shipped-goods places are created together with the warehouse.',
  'warehouses.creating': 'Creating…',
  'warehouses.createSubmit': 'Create warehouse',
  'warehouses.systemTitle': 'System places',
  'warehouses.systemIntro': 'The system zones of this warehouse: receiving, picked goods, packing, quarantine and shipped goods. Every warehouse has its own set. Operations use them automatically; you do not need to create cells for them.',
  'warehouses.systemPlacesLine': 'System zones: {list}',
  'warehouses.sys.RECEIVING': 'Receiving',
  'warehouses.sys.STAGING': 'Picked goods',
  'warehouses.sys.PACKING': 'Packing',
  'warehouses.sys.QUARANTINE': 'Quarantine (general)',
  'warehouses.sys.VIRTUAL': 'Shipped goods',
  'warehouses.sysHelp.RECEIVING': 'Where goods land when they are received. Not available for orders until put away.',
  'warehouses.sysHelp.STAGING': 'Where picked goods wait for packing.',
  'warehouses.sysHelp.PACKING': 'Where goods are packed before shipment.',
  'warehouses.sysHelp.QUARANTINE': 'Default place for damaged or unclear goods. Never available for orders.',
  'warehouses.sysHelp.VIRTUAL': 'Not a physical place: records goods that have left the warehouse.',
  'warehouses.present': 'In place',
  'warehouses.missing': 'Missing',
  'warehouses.createMissing': 'Create missing places',
  'warehouses.otherTitle': 'Other places ({n})',
  'warehouses.otherIntro': 'Places created before zones existed (for example by a file import). They keep working as before and are not moved into a zone automatically.',
  'warehouses.kind.BIN': 'storage cell',
  'warehouses.kind.STAGING': 'picked goods',
  'warehouses.kind.RECEIVING': 'receiving',
  'warehouses.kind.PACKING': 'packing',
  'warehouses.kind.DOCK': 'dock',
  'warehouses.kind.QUARANTINE': 'quarantine',
  'warehouses.kind.VIRTUAL': 'shipped goods',
  'warehouses.doneTitle': 'The warehouse is ready to use',
  'warehouses.done.receive': 'Receive goods into “Receiving” (or straight into a defect/quarantine cell if they arrive damaged).',
  'warehouses.done.putaway': 'Put goods away into storage or FBS cells — only then are they available for orders.',
  'warehouses.done.pick': 'Orders are picked from storage and FBS cells, never from defect or quarantine cells.',
  'warehouses.done.isolate': 'Returns and damaged goods can be isolated in defect/quarantine cells.',
  'warehouses.done.tsd': 'Cell codes can be printed as labels and scanned on the handheld (TSD) of this warehouse.',
  'warehouses.step.created': 'Warehouse',
  'warehouses.step.zones': 'Zones',
  'warehouses.step.cells': 'Cells',
  'warehouses.step.ready': 'Ready',
  'warehouses.stepsLabel': 'Setup progress',
  'warehouses.err.network': 'The connection was lost. Nothing was duplicated — you can press the button again.',
  'warehouses.err.outOfScope': 'This warehouse is outside your access. You can only set up your own warehouse.',
  'warehouses.err.quota': 'Your plan does not allow more warehouses. See Subscription.',
  'warehouses.err.timeZone': 'Choose a time zone from the list.',
  'warehouses.err.checkFields': 'Please check the fields.',
  'code.autoNote': 'Suggested automatically from the name. You can keep it.',
  'code.formatHint': 'Latin letters, digits and hyphens only, up to 24 characters.',
  'code.taken': 'This code is already used. Free alternative: {suggestion}',
  'code.free': 'The code is free.',
  'code.useSuggested': 'Use the suggested code',
  'code.hintWarehouse': 'A short name for this warehouse in lists and documents. It cannot be changed after creation.',
  'code.hintSeller': 'A short name for this seller. It is used to match rows when products and stock are loaded from a file and appears in document numbers, so it cannot be changed after creation.',
  'code.takenOnSubmit': 'Code {code} is already used. Try {suggestion} or leave the field on automatic.',
  'zones.title': 'Zones',
  'zones.intro': 'Split the warehouse into areas: main storage, FBS, defects, quarantine and others. A zone is a physical area — it does not change who owns the goods or how orders reserve them.',
  'zones.none': 'No zones yet. Start with main storage.',
  'zones.readOnly': 'Only an administrator can change the structure.',
  'zones.purpose.STORAGE': 'Main storage',
  'zones.purpose.FBS': 'FBS',
  'zones.purpose.DEFECT': 'Defects',
  'zones.purpose.QUARANTINE': 'Quarantine',
  'zones.purpose.OTHER': 'Other storage',
  'zones.help.STORAGE': 'Regular storage cells: goods put away here are available for orders.',
  'zones.help.FBS': 'An area for goods you prepare for marketplace (FBS) orders. It works like main storage: same owner, same reservation — any order may take goods from here. There is no separate FBS stock.',
  'zones.help.DEFECT': 'Isolated cells for damaged goods. Goods here are never available for orders.',
  'zones.help.QUARANTINE': 'Isolated cells for goods on hold (checks, disputes). Never available for orders.',
  'zones.help.OTHER': 'Any other storage area. Works like main storage.',
  'zones.can.receive': 'receive directly',
  'zones.can.store': 'store',
  'zones.can.pick': 'pick for orders',
  'zones.can.isolate': 'isolate',
  'zones.yes': 'yes',
  'zones.no': 'no',
  'zones.capabilitiesLabel': 'What this zone is for',
  'zones.cellsSummary': 'Cells: {n} (with goods: {withStock})',
  'zones.showCells': 'Show cells',
  'zones.cellHasStock': 'has goods',
  'zones.cellEmpty': 'empty',
  'zones.addCells': 'Add cells',
  'zones.defaultName.STORAGE': 'Main storage',
  'zones.defaultName.FBS': 'FBS',
  'zones.defaultName.DEFECT': 'Defects',
  'zones.defaultName.QUARANTINE': 'Quarantine',
  'zones.defaultName.OTHER': 'Storage',
  'zones.created': 'Zone “{name}” ({code}) is added. Now create its cells.',
  'zones.add': 'Add zone',
  'zones.addTitle': 'New zone',
  'zones.purposeLabel': 'What is this zone for?',
  'zones.name': 'Zone name',
  'zones.code': 'Zone code (optional)',
  'zones.codeAuto': 'automatic',
  'zones.codeHint': 'Starts every cell code in this zone, e.g. ST-A-01-1. Leave empty and it is chosen for you (ST, FBS, DEF, QC).',
  'zones.addSubmit': 'Add zone',
  'zones.addCellsFor': 'Add cells to {zone}',
  'zones.cellsExplain': 'A cell code is zone-row-rack-level. Enter one row and the range of racks and levels.',
  'zones.row': 'Row',
  'zones.racksFrom': 'Racks from',
  'zones.racksTo': 'Racks to',
  'zones.levelsFrom': 'Levels from',
  'zones.levelsTo': 'Levels to',
  'zones.racks': 'Racks',
  'zones.levels': 'Levels',
  'zones.from': 'from',
  'zones.to': 'to',
  'zones.tooMany': '{n} cells — at most 500 at once. Split the range.',
  'zones.preview': '{n} cells will be created: {first} … {last}',
  'zones.cellsCreatedSome': 'Created {n} cells; {existed} already existed and were kept.',
  'zones.cellsCreated': 'Created {n} cells.',
  'zones.createCells': 'Create cells',
  'zones.close': 'Close',
  'zones.err.cellsTaken': 'These codes are already used by other places: {codes}. Nothing was created — choose another row or range.',
  'zones.err.zoneCodeTaken': 'Zone code {code} is already used in this warehouse.',
  'zones.err.zoneCodeInvalid': 'A zone code is 1–8 Latin letters or digits.',
  'zones.rename': 'Rename',
  'zones.renameFor': 'Rename zone {zone}',
  'zones.renameHint': 'Only the display name changes. The zone keeps its purpose (“{purpose}”) and its code {code}, so receiving, picking and reports work exactly as before.',
  'zones.renamed': 'Zone renamed to “{name}”.',
  'cells.manage': 'Manage cell {code}',
  'cells.panelTitle': 'Cell {code}',
  'cells.codeLabel': 'Cell code',
  'cells.renameWarning': 'The code is printed on the cell label. If you change it, labels already printed stop scanning — print new ones.',
  'cells.pickable': 'Goods in this cell can be picked for orders',
  'cells.sequence': 'Walking order (smaller = earlier on the pick route)',
  'cells.saved': 'Saved.',
  'cells.labelsOutdated': 'Saved. The cell has history, so reprint its label: the old label no longer scans. New code: {code}.',
  'cells.delete': 'Delete',
  'cells.deleteHint': 'This cell was never used, so it can be deleted completely.',
  'cells.deleteBlocked.HISTORY': 'It cannot be deleted: the cell was already used (stock, movements, tasks or documents). Archive it instead — its history stays.',
  'cells.deleteBlocked.STOCK': 'It cannot be deleted: there is stock in this cell.',
  'cells.deleteConfirm': 'Delete cell {code} for good? This cannot be undone.',
  'cells.confirmDelete': 'Yes, delete the cell',
  'cells.deletedDone': 'Cell {code} deleted.',
  'cells.archive': 'Archive',
  'cells.archiveExplain': 'Archive cell {code}? An archived cell disappears from receiving, putaway, picking, moves and counts. Its history stays and you can restore it later.',
  'cells.confirmArchive': 'Yes, archive',
  'cells.archiveBlocked.STOCK': 'It cannot be archived yet: there is still stock here. Move it to another cell or write it off first.',
  'cells.archiveBlocked.OPEN_WORK': 'It cannot be archived yet: there is open work here (a task or an unfinished count). Finish it first.',
  'cells.archivedDone': 'Cell {code} archived. Its history stays.',
  'cells.archivedTitle': 'Archived cells ({n})',
  'cells.archivedIntro': 'These cells are not used by any operation. Their history stays visible. Restore a cell to use it again.',
  'cells.archivedOn': 'Archived {date}',
  'cells.restore': 'Restore',
  'cells.restoreAria': 'Restore cell {code}',
  'cells.restoredDone': 'Cell {code} restored.',
  'cells.err.codeTaken': 'Code {code} is already used by another place in this warehouse.',
  'cells.err.codeInvalid': 'A cell code is Latin letters, digits and hyphens, up to 40 characters.',
  'cells.err.system': 'This is a system place of the warehouse (receiving, picked goods, packing, quarantine, shipped goods). It cannot be changed, archived or deleted.',
  'cells.err.archived': 'This cell is archived. Restore it first.',
  'cells.err.reserved': 'Goods in this cell are reserved for orders, so it cannot be made non-pickable now.',
  'cells.err.purposeLocked': 'The purpose of a zone that already has cells cannot be changed. Rename the zone or create a new one.',
  'labels.title': 'Cell labels',
  'labels.intro': 'Print shelf labels: the cell code in large type and a barcode that never changes when the cell is renamed. Stick one on every cell and scan it on the handheld instead of typing the code.',
  'labels.layoutLegend': 'Format',
  'labels.layout.A4_8UP': 'A4 sheet, 8 labels',
  'labels.layoutHint.A4_8UP': 'For an office printer. Cut along the thin lines.',
  'labels.layout.LABEL_100x60': 'Label 100 × 60 mm',
  'labels.layoutHint.LABEL_100x60': 'One label per page, for a label printer.',
  'labels.scopeLegend': 'What to print',
  'labels.scope.WAREHOUSE': 'Whole warehouse ({n} places)',
  'labels.scope.ZONE': 'One zone',
  'labels.scope.SELECTED': 'Selected cells',
  'labels.zonePick': 'Zone',
  'labels.selectAllIn': 'Select all in {zone}',
  'labels.systemGroup': 'System places',
  'labels.otherGroup': 'Other places',
  'labels.count': 'Labels: {n} (at most {max} at once)',
  'labels.tooMany': 'At most {max} labels at once. Choose a zone or fewer cells.',
  'labels.nothing': 'There is nothing to print here yet.',
  'labels.download': 'Download labels',
  'labels.preparing': 'Preparing the PDF…',
  'labels.done': 'Labels in the PDF: {n}, pages: {pages}.',
  'labels.doneArchived': '{n} archived cell(s) were included and are marked ARCHIVED on the label.',
  'labels.cellBarcode': 'Barcode',
  'labels.printOne': 'Print label',
  'err.locArchived': 'This location is archived and cannot be used. Choose another one.',
  'queue.slaHelpButton': 'What do these statuses mean?',
  'queue.slaHelpTitle': 'How the deadline status is worked out',
  'queue.slaHelpIntro': 'This is not a priority that someone sets by hand. The system recalculates it on every refresh from the order’s shipping deadline and the current time, so it changes by itself and cannot be edited.',
  'queue.slaHelpBasis': 'Work has to start before the deadline by the time the warehouse needs to pick, pack and ship. The deadline comes from the marketplace; when the marketplace gives none, an estimate is used and marked “estimated”.',
  'queue.slaHelp.normal': 'Plenty of time, no deadline known, or the order is already shipped or cancelled.',
  'queue.slaHelp.approaching_cutoff': 'Less than {approaching} hours remain before work on the order must start.',
  'queue.slaHelp.urgent': 'Less than {urgent} hours remain before work on the order must start.',
  'queue.slaHelp.blocked': 'Something stops the order: a short pick, missing marking or label, an unfinished package, a cancellation after work began, or a changed deadline. The reason is listed in “Blockers”.',
  'queue.slaHelp.overdue': 'The shipping deadline has passed. Shown even when there is also a problem.',
  'zones.err.range': 'Row: 1–3 letters or digits; racks 1–99; levels 1–20; at most {max} cells at once.',
  'sellers.create': 'Add seller',
  'sellers.createTitle': 'New seller',
  'sellers.name': 'Seller name',
  'sellers.createSubmit': 'Add seller',
  'sellers.created': 'Seller “{name}” is added with code {code}. Next: a contract with a tariff and a Seller Portal login (on the seller’s page).',
  // ADR-084 - staff sign-in, invitations, access
  'login.passwordFormLabel': 'Sign in with email and password',
  'login.email': 'Email',
  'login.password': 'Password',
  'login.organization': 'Company',
  'login.organizationHint': 'You have access to several companies. Choose where to sign in.',
  'login.staysSignedIn': 'This device stays signed in for up to 30 days. Sign out on a shared computer.',
  'login.forgot': 'Forgot your password?',
  'login.forgotHelp': 'Ask the administrator of your company: in Users & Roles they can give you a one-time link to set a new password.',
  'login.useServiceKey': 'Sign in with a service key',
  'login.keyFormLabel': 'Sign in with a service key',
  'login.serviceKeyHint': 'For integrations and the transition of an existing administrator. Employees sign in with email and password.',
  'login.backToPassword': 'Back to email and password',
  'accept.inviteTitle': 'Join your company in LATYNEX WMS',
  'accept.recoverTitle': 'Set a new password',
  'accept.state.EXPIRED': 'This link has expired. Ask your administrator for a new one.',
  'accept.state.USED': 'This link has already been used.',
  'accept.state.REVOKED': 'This link was withdrawn. Ask your administrator for a new one.',
  'accept.state.INVALID': 'This link is not valid. Check that you copied it completely.',
  'accept.toLogin': 'Go to sign-in',
  'accept.company': 'Company',
  'accept.yourName': 'Your name',
  'accept.password': 'Password',
  'accept.newPassword': 'New password',
  'accept.passwordHintInvite': 'Choose a password of at least 10 characters. If you already have a LATYNEX WMS account with this email, enter its password instead.',
  'accept.passwordHint': 'At least 10 characters, not your email. A long phrase is easier to remember and harder to guess.',
  'accept.repeat': 'Repeat the password',
  'accept.mismatch': 'The passwords do not match.',
  'accept.join': 'Join and sign in',
  'accept.save': 'Save and sign in',
  'accept.signedInWarning': 'Someone is already signed in on this device. Continuing signs them out here.',
  'staff.subtitle': 'Invite employees, give them a role and a warehouse, block or revoke access.',
  'staff.subtitleScoped': 'You manage the employees of {warehouse}.',
  'staff.invite': 'Invite employee',
  'staff.inviteTitle': 'Invite an employee',
  'staff.name': 'Name',
  'staff.emailHint': 'The employee signs in with this email.',
  'staff.role': 'Role',
  'staff.access': 'Access',
  'staff.wholeCompany': 'Whole company (all warehouses)',
  'staff.scopedOnly': 'Only the {warehouse} warehouse - your own access is limited to it.',
  'staff.accessHint': 'An employee limited to one warehouse works and manages people only there.',
  'staff.review': 'Check',
  'staff.reviewNote': 'You will get a one-time link valid for 72 hours. No email is sent: you hand the link to the employee yourself.',
  'staff.createInvite': 'Create invitation',
  'staff.edit': 'Change',
  'staff.linkReadyInvite': 'Invitation for {name} is ready',
  'staff.linkReadyRecover': 'Access link for {name} is ready',
  'staff.linkLabel': 'One-time link',
  'staff.copy': 'Copy link',
  'staff.copied': 'Copied.',
  'staff.copyManually': 'Could not copy automatically - select the link and copy it.',
  'staff.linkValid': 'Valid until {date}; works once.',
  'staff.linkOnce': 'It is shown only now. If it gets lost, create a new link - the old one stops working.',
  'staff.linkHandOver': 'Hand it over personally or through a messenger you trust - whoever opens it first gets the access.',
  'staff.linkNotProof': 'The link does not confirm that the person owns this email.',
  'staff.linkDone': 'Done',
  'staff.invitations': 'Invitations',
  'staff.noInvitations': 'No invitations yet.',
  'staff.expiresAt': 'link valid until {date}',
  'staff.inviteStatus.PENDING': 'Waiting',
  'staff.inviteStatus.ACCEPTED': 'Accepted',
  'staff.inviteStatus.REVOKED': 'Withdrawn',
  'staff.inviteStatus.EXPIRED': 'Expired',
  'staff.newLink': 'New link',
  'staff.revokeInvite': 'Withdraw',
  'staff.confirmRevokeInvite': 'Withdraw the invitation for {name}? The link stops working.',
  'staff.members': 'Employees',
  'staff.you': 'you',
  'staff.oneWarehouse': 'One warehouse',
  'staff.roleGuide': 'What each role can do',
  'staff.scopeHelp': 'Any role can be limited to one warehouse. A warehouse administrator manages people only in that warehouse and cannot create company-wide administrators.',
  'staff.roleHelp.ADMIN': 'Everything in Ops, including employees, warehouses, sellers, billing and settings.',
  'staff.roleHelp.WAREHOUSE': 'Receiving, putaway, returns, stock and stock corrections, creating orders.',
  'staff.roleHelp.PICKER': 'Picking orders; can view stock and orders.',
  'staff.roleHelp.PACKER': 'Packing, labels and shipping; can view stock and orders.',
  'staff.roleHelp.BILLING': 'Tariffs, charges and invoices; can view sellers and operations evidence, cannot change warehouse operations.',
  'staff.roleHelp.SUPPORT': 'Views sellers, orders, stock and billing to answer questions; changes nothing.',
  'staff.notFoundOrOutside': 'This employee was not found or is outside your access.',
  'staff.accessTitle': 'Access',
  'staff.state.ACTIVE': 'Active',
  'staff.state.SUSPENDED': 'Blocked - cannot sign in',
  'staff.state.REVOKED': 'Access revoked',
  'staff.selfNote': 'You cannot change your own role or access. Another administrator can.',
  'staff.actionsTitle': 'Manage access',
  'staff.changeRole': 'Role',
  'staff.roleChanged': 'Role changed. It applies from the employee\'s next action.',
  'staff.recoveryLink': 'Link to set a new password',
  'staff.recoveryCreated': 'A one-time link was created. Hand it over to the employee.',
  'staff.suspend': 'Block',
  'staff.confirmSuspend': 'Block {name}? They are signed out on every device at once.',
  'staff.suspended': 'Blocked. The employee was signed out everywhere.',
  'staff.restore': 'Unblock',
  'staff.restored': 'Unblocked. The employee signs in again with their password.',
  'staff.revokeAccess': 'Revoke access',
  'staff.confirmRevoke': 'Revoke access for {name}? They are signed out everywhere; to come back they need a new invitation.',
  'staff.revoked': 'Access revoked.',
  'staff.actionsHelp': 'Blocking is temporary. Revoking ends access; the history stays. The last company administrator cannot be blocked or demoted.',
  'staff.revokedHelp': 'To give access again, send a new invitation.',
  'staff.readOnly': 'Only an administrator can change access.',
  'staff.err.network': 'The connection was lost. Try again.',
  'staff.err.invalidCredentials': 'Wrong email or password. After several attempts sign-in pauses for 15 minutes.',
  'staff.err.suspended': 'Your access is blocked. Contact your company administrator.',
  'staff.err.passwordWeak': 'Choose a password of at least {min} characters that is not your email.',
  'staff.err.currentPasswordWrong': 'The current password is not correct.',
  'staff.err.linkNotValid': 'This link is no longer valid. Ask your administrator for a new one.',
  'staff.err.recoveryOtherCompany': 'This person also works for another company, so a link from your company cannot set their password. They can change it in their account, or contact LATYNEX support.',
  'staff.err.recoveryNotActive': 'Unblock the employee first.',
  'staff.err.alreadyMember': 'This person already has access to your company. Open them in the list to change the role.',
  'staff.err.cannotAccept': 'This invitation cannot be accepted with this email. Contact the administrator who sent it.',
  'staff.err.outOfScope': 'This is outside your warehouse. You can manage only the employees of your own warehouse.',
  'staff.err.self': 'You cannot change your own access.',
  'staff.err.lastAdmin': 'This is the last company administrator: appoint another administrator first.',
  'staff.err.quota': 'Your plan does not allow more employees. See Subscription.',
  'staff.err.forbidden': 'Your role does not allow this.',
  'staff.err.tooMany': 'Too many attempts. Wait a minute and try again.',
  'staff.err.checkFields': 'Please check the fields.',
  'settings.devicesTitle': 'Devices',
  'settings.devicesHelp': 'If you signed in on a device you no longer control, sign out everywhere.',
  'settings.confirmLogoutAll': 'Sign out on every device, including this one?',
  'settings.logoutAll': 'Sign out on all devices',
  'settings.passwordChanged': 'Password changed. Signed out on {n} other device(s).',
  'settings.passwordSet': 'Password set. You can now sign in with your email and password.',
  'settings.changePassword': 'Change password',
  'settings.setPassword': 'Set a password',
  'settings.viaKeyNote': 'You are signed in with a service key. Set a password to sign in with your email - the key keeps working for integrations.',
  'settings.currentPassword': 'Current password',
  // Products (SKU import)
  'skuImport.subtitle': 'Add or update your product catalogue from an Excel file. Nothing is saved until you confirm.',
  'skuImport.step1': '1. Download the template',
  'skuImport.templateHint': 'One row per product; for clothes one row per size and colour (the article repeats). Article and name are required. Weight in kg, sizes in cm. Up to 500 rows per file.',
  'skuImport.downloadTemplate': 'Download Excel template',
  'skuImport.step2': '2. Choose what to do with existing products',
  'skuImport.onExisting': 'Products that already exist',
  'skuImport.onExisting.skip': 'Keep existing products unchanged',
  'skuImport.onExisting.update': 'Update existing products (name, weight, size, colour)',
  'skuImport.step3': '3. Upload the filled file',
  'skuImport.fileHint': 'Excel file (.xlsx), up to 512 KB. Every row is checked before anything is saved.',
  'skuImport.upload': 'Upload Excel file',
  'skuImport.checking': 'Checking the file…',
  'skuImport.previewTitle': 'Check result',
  'skuImport.summary': '{new} new, {update} to update, {skip} unchanged, {error} errors',
  'skuImport.nothingSaved': 'Nothing has been saved yet. Press the button below to import.',
  'skuImport.hasErrors': 'Some rows have errors, so nothing can be imported. Fix the rows marked as errors in your file and upload it again.',
  'skuImport.alreadyImported': 'This file was already imported, nothing to do.',
  'skuImport.col.row': 'Row',
  'skuImport.col.code': 'Code',
  'skuImport.col.name': 'Name',
  'skuImport.col.barcode': 'Barcode',
  'skuImport.col.status': 'Status',
  'skuImport.status.NEW': 'New',
  'skuImport.status.UPDATE': 'Update',
  'skuImport.status.SKIP': 'Unchanged',
  'skuImport.status.ERROR': 'Error',
  'skuImport.import': 'Import {n} products',
  'skuImport.importing': 'Importing…',
  'skuImport.uploadAnother': 'Upload another file',
  'skuImport.done': 'Imported: {created} new, {updated} updated.',
  'skuImport.doneSkipped': '{skipped} unchanged.',
  'skuImport.doneReplayed': 'This import was already completed earlier.',
  'skuImport.importMore': 'Import more',
  'skuImport.row.code': 'The article (code) is missing or longer than 64 characters.',
  'skuImport.row.name': 'The name is missing or longer than 200 characters.',
  'skuImport.row.weight': 'Weight must be a number of kilograms above zero (for example 0,35).',
  'skuImport.row.length': 'Length must be a number of centimetres above zero (for example 12,5).',
  'skuImport.row.width': 'Width must be a number of centimetres above zero (for example 12,5).',
  'skuImport.row.height': 'Height must be a number of centimetres above zero (for example 12,5).',
  'skuImport.row.barcode': 'The barcode is not valid.',
  'skuImport.row.size': 'The size is too long.',
  'skuImport.row.color': 'The colour is too long.',
  'skuImport.row.variant': 'This size/colour combination would get the same code as a different product. Change the article or add a "SKU code" column.',
  'skuImport.row.duplicateKey': 'The same variant (article + size + colour) or the same barcode appears more than once in the file.',
  'skuImport.row.duplicateBarcode': 'This barcode already belongs to another product.',
  'skuImport.row.generic': 'This row could not be imported.',
  'skuImport.file.notXlsx': 'Choose an Excel file in .xlsx format.',
  'skuImport.file.tooLarge': 'The file is too large (max 512 KB).',
  'skuImport.file.macros': 'The file contains macros. Save it as a regular .xlsx file without macros.',
  'skuImport.file.externalLinks': 'The file links to other files. Remove the external links and try again.',
  'skuImport.file.embedded': 'The file contains embedded objects. Remove them and try again.',
  'skuImport.file.corrupt': 'The file could not be read. Make sure it is a valid .xlsx file.',
  'skuImport.file.tooManyRows': 'Too many rows - at most {max} products per file.',
  'skuImport.file.headerNotFound': 'The header row was not found. Use the template: it needs the columns "SKU code" and "Product name".',
  'skuImport.file.empty': 'The file has no products.',
  'skuImport.file.formula': 'Row {row} contains a formula. Replace formulas with plain values and try again.',
  'skuImport.file.noSheet': 'The file has no sheet to read.',
  'skuImport.file.alreadyUsed': 'This file was already used for an import.',
  'skuImport.err.hasErrors': 'The file has rows with errors, so it cannot be imported. Fix the file and upload it again.',
  'skuImport.err.stale': 'The catalogue changed since the check - upload the file again.',
  'skuImport.err.partial': 'Some products could not be saved. Upload the file again to see what is left to import.',
  'skuImport.err.tooMany': 'Too many checks in a short time. Try again in a minute.',
  'skuImport.err.network': 'The connection was lost. Check your internet and try again.',
  'skuImport.err.generic': 'Something went wrong. Try again in a moment.',
  'imports.fromExcel': 'Import products from Excel',
  'skuImport.title': 'Import products from Excel',
  'skuImport.seller': 'Seller',
  'skuImport.chooseSeller': 'Choose a seller',
  'skuImport.sellerRequired': 'Choose a seller first, then upload the file.',
  'skuImport.excelCardHint': 'Download a template, fill it in, check every row, then confirm.',
  'skuImport.backToImports': 'Back to imports',
  'skuImport.noWrite': 'Your role cannot import products.',
  'skuImport.err.ownerNotAllowed': 'This seller cannot be used for the import.',
  'skuImport.err.ownerRequired': 'Choose a seller first.',

  // Pilot exceptions (stock corrections, order shortfalls, order cancel)
  'nav.exceptions': 'Exceptions',
  'reason.RECOUNT': 'Recount',
  'reason.FOUND': 'Found stock',
  'reason.LOST': 'Lost',
  'reason.DAMAGED': 'Damaged',
  'reason.MISPLACED': 'Was in the wrong place',
  'reason.EXPIRED': 'Expired',
  'reason.DATA_ERROR': 'Data entry error',
  'reason.OTHER': 'Other (write a note)',
  'cancelReason.CLIENT_REQUEST': 'Client request',
  'cancelReason.OUT_OF_STOCK': 'Out of stock',
  'cancelReason.DUPLICATE': 'Duplicate order',
  'cancelReason.ENTERED_BY_MISTAKE': 'Entered by mistake',
  'cancelReason.OTHER': 'Other (write a note)',
  'stx.actionsCol': 'Actions',
  'stx.countAction': 'Count / correct',
  'stx.moveAction': 'Move',
  'stx.quarantineAction': 'Quarantine',
  'stx.writeOffAction': 'Write off',
  'stx.title.COUNT': 'Count and correct stock',
  'stx.title.MOVE': 'Move stock',
  'stx.title.QUARANTINE': 'Move to quarantine',
  'stx.title.WRITE_OFF': 'Write off stock',
  'stx.fieldProduct': 'Product',
  'stx.fieldLocation': 'Location',
  'stx.fieldOwner': 'Seller',
  'stx.systemQty': 'System quantity',
  'stx.onHandQty': 'On hand',
  'stx.actualQty': 'Actual quantity (counted)',
  'stx.qty': 'Quantity',
  'stx.destination': 'Destination location',
  'stx.destinationPlaceholder': 'Choose a location',
  'stx.destinationLoading': 'Loading locations…',
  'stx.reason': 'Reason',
  'stx.reasonPlaceholder': 'Choose a reason',
  'stx.note': 'Note',
  'stx.noteRequired': 'Note (required)',
  'stx.continue': 'Continue',
  'stx.back': 'Back',
  'stx.cancel': 'Cancel',
  'stx.close': 'Close',
  'stx.working': 'Working…',
  'stx.confirmCount': 'Confirm count',
  'stx.confirmMove': 'Confirm move',
  'stx.confirmQuarantine': 'Confirm quarantine',
  'stx.confirmWriteOff': 'Write off {qty} pcs',
  'stx.confirmCountText': 'System {system} → actual {actual} ({delta})',
  'stx.confirmCountSame': 'System {system} → actual {actual}: no change, the count will simply be confirmed.',
  'stx.confirmMoveText': 'Move {qty} pcs from {from} to {to}.',
  'stx.confirmQuarantineText': 'Move {qty} pcs from {from} to a quarantine location.',
  'stx.confirmWriteOffText': 'This removes {qty} pcs of {sku} from stock at {from}; it is recorded in the ledger and cannot be undone.',
  'stx.reasonLine': 'Reason: {reason}',
  'stx.noteLine': 'Note: {note}',
  'stx.doneCount': 'Counted: system {system} → actual {actual} ({delta})',
  'stx.doneCountSame': 'Count confirmed: {qty} pcs, no change.',
  'stx.doneMove': 'Moved {qty} pcs from {from} to {to}.',
  'stx.doneQuarantine': 'Moved {qty} pcs from {from} to quarantine.',
  'stx.doneWriteOff': 'Written off {qty} pcs from {from}; recorded in the ledger.',
  'stx.doneBroken': 'Reservations affected: {n}. Orders that now lack stock are listed under Exceptions → Order shortfalls.',
  'stx.doneFlags': 'The “needs count” mark for this cell was cleared.',
  'stx.openExceptions': 'Open Exceptions',
  'stx.invalidQty': 'Enter a whole number (0 or more).',
  'stx.qtyPositive': 'Enter a whole quantity of at least 1.',
  'stx.reasonMissing': 'Choose a reason.',
  'stx.noteMissing': 'Write a short note (at least 3 characters).',
  'stx.destMissing': 'Choose a destination location.',
  'stx.trackedHint': 'Serial- or lot-tracked products cannot be corrected here.',
  'found.action': 'Found stock',
  'found.title': 'Found stock',
  'found.intro': 'Found items that the system shows as 0? Pick the product (check the seller carefully), the location, then count how many you found.',
  'found.search': 'Find a product (code, name or barcode)',
  'found.searchHint': 'Type at least 2 characters.',
  'found.noHits': 'Nothing found.',
  'found.seller': 'Seller: {name}',
  'found.select': 'Select',
  'found.selected': 'Selected: {sku} — seller {owner}',
  'found.change': 'Choose another product',
  'found.warehouse': 'Warehouse',
  'found.location': 'Location',
  'exc.title': 'Exceptions',
  'exc.noAccess': 'You have no access to this section.',
  'exc.tabCount': 'Needs count',
  'exc.tabShortfalls': 'Order shortfalls',
  'exc.tabHistory': 'History',
  'exc.flagsEmpty': 'Nothing needs counting.',
  'exc.flagsHint': 'Cells appear here after a short pick or a manual mark, until someone counts them.',
  'exc.shortEmpty': 'No order lines are short.',
  'exc.histEmpty': 'No corrections yet.',
  'exc.histFilter': 'Filter by product',
  'exc.colLocation': 'Location',
  'exc.colProduct': 'Product',
  'exc.colSeller': 'Seller',
  'exc.colWhy': 'Why',
  'exc.colSince': 'Since',
  'exc.colOrder': 'Order',
  'exc.colActions': 'Actions',
  'exc.colOrdered': 'Ordered',
  'exc.colPicked': 'Picked',
  'exc.colReserved': 'Reserved',
  'exc.colMissing': 'Missing',
  'exc.colWhen': 'When',
  'exc.colWho': 'Who',
  'exc.colKind': 'Action',
  'exc.colChange': 'Change',
  'exc.colReason': 'Reason',
  'exc.colNote': 'Note',
  'exc.handheld': 'Handheld',
  'exc.flagReason.SHORT_PICK': 'Short pick',
  'exc.flagReason.MANUAL': 'Marked manually',
  'exc.kind.COUNT': 'Count',
  'exc.kind.MOVE': 'Move',
  'exc.kind.QUARANTINE': 'Quarantine',
  'exc.kind.WRITE_OFF': 'Write-off',
  'exc.countNow': 'Count now',
  'exc.marketplaceBadge': 'Marketplace',
  'exc.reallocate': 'Look for it in other stock',
  'exc.reduce': 'Reduce the line to what exists',
  'exc.cancelLine': 'Cancel the line',
  'exc.reduceDisabled': 'Marketplace order: the marketplace sets the quantity, it cannot be reduced here.',
  'exc.cancelLineDisabled': 'Part of this line is already picked, so it cannot be cancelled.',
  'exc.title.REALLOCATE': 'Look for it in other stock',
  'exc.title.REDUCE': 'Reduce the line',
  'exc.title.CANCEL_LINE': 'Cancel the line',
  'exc.intro.REALLOCATE': 'The system will try to take the missing {qty} pcs from other stock and create new pick tasks.',
  'exc.intro.REDUCE': 'The line will be reduced to {keep} pcs (what is picked plus what is still reserved). The order will ship {qty} pcs short.',
  'exc.intro.CANCEL_LINE': 'The line will be cancelled and its reservation released. Nothing was picked for it. If it is the last open line, the order is cancelled.',
  'exc.confirm.REALLOCATE': 'Look for it',
  'exc.confirm.REDUCE': 'Reduce the line',
  'exc.confirm.CANCEL_LINE': 'Cancel the line',
  'exc.done.REALLOCATE': 'Found in other stock: {n} new pick task(s) created.',
  'exc.done.REALLOCATE_NONE': 'The missing quantity was reserved from other stock.',
  'exc.done.stillMissing': 'Still missing: {n} pcs.',
  'exc.done.REDUCE': 'Line reduced. The order is now: {state}.',
  'exc.done.CANCEL_LINE': 'Line cancelled. The order is now: {state}.',
  'ordCancel.button': 'Cancel order',
  'ordCancel.marketplace': 'This order came from a marketplace — the marketplace cancels it, not Ops.',
  'ordCancel.title': 'Cancel order {number}',
  'ordCancel.consequenceRelease': 'Reservations will be released. Nothing has been picked yet.',
  'ordCancel.consequencePicked': 'Picked goods are still in staging/packing: after cancelling, move them back to a bin (Move) and confirm.',
  'ordCancel.confirm': 'Cancel the order',
  'ordCancel.keep': 'Keep the order',
  'ordCancel.doneCancelled': 'Order cancelled. Reservations released: {n}.',
  'ordCancel.doneAfterPick': 'Order cancelled — the picked goods must be returned to stock.',
  'ordCancel.doneFinished': 'Cancellation finished — the goods are back in stock.',
  'ordCancel.bannerTitle': 'Cancelled — goods must be returned to stock',
  'ordCancel.bannerText': 'The picked goods are still in staging/packing. Move them back to a bin, then finish the cancellation.',
  'ordCancel.goMove': 'Go to Inventory to move the goods back',
  'ordCancel.finish': 'Goods are back on the shelf — finish cancellation',
  'ordCancel.finishTitle': 'Finish cancellation of order {number}',
  'ordCancel.finishText': 'Confirm that every picked item is back in its bin. The order will become Cancelled.',
  'ordCancel.finishConfirm': 'Confirm — goods are back',
  'ordCancel.lineCancelled': 'Cancelled',
  'err.adjustBelowHeld': 'The correction would leave less on hand than the held surplus ({held}). Decide the receipt surplus and record its withdrawal first, then repeat.',
  'err.overageHeld': 'Part of this stock ({held}) is held as an unresolved receiving surplus and cannot be moved or written off. Decide the surplus on the inbound first.',
  'err.countStale': 'Stock changed in the meantime. The system quantity is now {current}. Count again.',
  'err.insufficient': 'Not enough stock: available {available}, requested {requested}.',
  'err.reasonRequired': 'Choose a reason.',
  'err.noteRequired': 'Write a short note — it is required for this reason.',
  'err.writeOffOfficeOnly': 'Write-offs are done in the office, not on the handheld.',
  'err.trackedUnsupported': 'Serial- or lot-tracked products cannot be corrected here.',
  'err.locOtherWarehouse': 'That location belongs to another warehouse.',
  'err.locNotQuarantine': 'That is not a quarantine location.',
  'err.locationNotAllowed': 'This location cannot be used for this action.',
  'err.quantityInvalid': 'The quantity is not valid.',
  'err.notFound': 'Not found — it may have been changed or removed. Refresh the page.',
  'err.validation': 'Some of the entered data is not valid. Check the fields and try again.',
  'err.cannotCancel': 'This order cannot be cancelled in its current state ({state}).',
  'err.channelForbidden': 'This is a marketplace order — it is cancelled by the marketplace, not here.',
  'err.reallocImpossible': 'Not found elsewhere — choose reduce or cancel.',
  'err.reduceNotAllowed': 'This line cannot be reduced here.',
  'err.linePicked': 'Part of this line is already picked, so it cannot be cancelled.',
  'err.lineCancelled': 'This line is already cancelled.',
  'err.invalidState': 'The order is in a state where this is not possible ({state}).',
  'err.rateLimited': 'Too many requests. Wait a moment and try again.',
  'err.forbidden': 'Your role does not allow this action.',
  'err.network': 'Could not reach the server. Check your connection and try again — nothing was applied twice.',
  'err.generic': 'Something went wrong. Try again; if it repeats, tell your administrator.',
  'sellerDetail.tabContract': 'Contract',
  'onboard.title': 'Readiness to receive requests',
  'onboard.step.CLIENT': 'Client',
  'onboard.step.WAREHOUSE': 'Serving warehouse',
  'onboard.step.CONTRACT': 'Contract',
  'onboard.step.PORTAL': 'Portal access',
  'onboard.step.READY': 'Ready for requests',
  'onboard.state.done': 'Done',
  'onboard.state.missing': 'Missing',
  'onboard.detail.warehouseNone': 'No warehouse assigned',
  'onboard.detail.contractNumber': 'No. {number} of {date}',
  'onboard.detail.contractNumberOnly': 'No. {number}',
  'onboard.detail.portalActive': 'Users with access: {n}',
  'onboard.detail.portalPending': 'Invited, not signed in yet: {n}',
  'onboard.detail.portalNone': 'Nobody has access yet',
  'onboard.readyYes': 'Yes — the client can send requests',
  'onboard.readyNo': 'Not yet — the client cannot send requests',
  'onboard.reason.NONE': 'There is no contract yet',
  'onboard.reason.DRAFT': 'The contract is a draft',
  'onboard.reason.EXPIRED': 'The contract has expired',
  'onboard.reason.SUSPENDED': 'The contract is suspended',
  'onboard.reason.TERMINATED': 'The contract is terminated',
  'onboard.reason.NOT_STARTED': 'The contract has not started yet',
  'onboard.missingTitle': 'What is missing',
  'onboard.hint.WAREHOUSE': 'No warehouse serves this client yet. Assign one — that creates a contract with the warehouse’s legal entity.',
  'onboard.hint.PORTAL': 'Nobody from this client can sign in to the Portal yet.',
  'onboard.fix.WAREHOUSE': 'Assign warehouse',
  'onboard.fix.CONTRACT': 'Set up contract',
  'onboard.fix.PORTAL': 'Invite to Portal',
  'onboard.entitlement.limited': 'Warehouses on your plan: {used} of {limit} used, {remaining} left',
  'onboard.entitlement.unlimited': 'Warehouses on your plan: no limit',
  'onboard.channels.required': 'Marketplaces can be connected only under an active contract.',
  'onboard.channels.free': 'Marketplaces can be connected without a contract (organisation setting).',
  'contract.title': 'Contracts with warehouses',
  'contract.new': 'Assign warehouse / new contract',
  'contract.empty': 'No contract yet. Assign a warehouse to start.',
  'contract.formNew': 'New contract',
  'contract.formEdit': 'Edit contract',
  'contract.field.warehouse': 'Warehouse',
  'contract.field.number': 'Contract number',
  'contract.field.date': 'Contract date',
  'contract.field.validFrom': 'Valid from',
  'contract.field.validTo': 'Valid until (optional)',
  'contract.field.state': 'Status',
  'contract.field.tariff': 'Tariff (optional)',
  'contract.field.legalEntity': 'Legal entity',
  'contract.field.warehouses': 'Warehouses',
  'contract.field.period': 'Period',
  'contract.field.file': 'Contract PDF',
  'contract.noTariff': 'Not set',
  'contract.noNumber': 'Number not set',
  'contract.state.DRAFT': 'Draft',
  'contract.state.ACTIVE': 'Active',
  'contract.state.SUSPENDED': 'Suspended',
  'contract.state.ON_HOLD': 'Suspended',
  'contract.state.TERMINATED': 'Terminated',
  'contract.state.EXPIRED': 'Expired',
  'contract.state.NOT_STARTED': 'Not started yet',
  'contract.periodOpen': '{from} — open-ended',
  'contract.periodRange': '{from} — {to}',
  'contract.activationHint': 'To make a contract active, enter its number and date.',
  'contract.terminatedNote': 'A terminated contract cannot be changed. Create a new one.',
  'contract.save': 'Save contract',
  'contract.saved': 'Contract saved.',
  'contract.pdf.upload': 'Upload PDF',
  'contract.pdf.replace': 'Replace PDF',
  'contract.pdf.download': 'Download PDF',
  'contract.pdf.none': 'No file attached',
  'contract.pdf.hint': 'PDF only, up to 5 MB.',
  'contract.pdf.uploaded': 'File uploaded.',
  'contract.err.terminated': 'This contract is terminated and can no longer be changed.',
  'contract.err.activationIncomplete': 'Enter the contract number and date before activating it.',
  'contract.err.alreadyActive': 'This client already has an active contract with this legal entity.',
  'contract.err.numberTaken': 'This contract number is already used for this legal entity.',
  'contract.err.document': 'The file must be a PDF of up to 5 MB.',
  'contract.err.period': 'The end date must be later than the start date.',
  'contract.err.forbidden': 'Your role cannot do this.',
  'contract.err.outOfScope': 'This contract belongs to a warehouse outside your access.',
  'contract.err.notFound': 'Not found. Refresh the page.',
  'contract.err.generic': 'Could not save. Check the fields and try again.',
  'settings.channelContractTitle': 'Marketplace connections',
  'settings.channelContractLabel': 'Require an active contract before a client can connect a marketplace',
  'settings.channelContractHint': 'On by default. Turn it off if you do not sign contracts with your clients.',
  'settings.channelContractSaved': 'Setting saved.',
  'sku.article': 'Art. {value}',
  'sku.size': 'Size {value}',
  'sku.color': 'Colour {value}',
  'sku.barcode': 'Barcode {value}',
  'sku.barcodes': 'Barcodes: {value}',
  'sku.barcodeMore': 'Barcode {value} +{n} more',
  'rcv.nextTitle': 'What to do next',
  'rcv.tsdPath': 'On the handheld (TSD): Receiving →',
  'rcv.copy': 'Copy number',
  'rcv.copied': 'Copied',
  'rcv.next.EXPECTED': 'The seller’s request is accepted and waiting for the goods. When the goods arrive, press “Start receiving”.',
  'rcv.next.ARRIVED': 'The goods have arrived. Press “Start receiving” to open the lines for receiving.',
  'rcv.next.RECEIVING': 'Receiving is in progress. Receive each line below with “Receive” (enter the quantity), or scan on the handheld. When every line is done, press “Finish receiving”.',
  'rcv.next.RECEIVED': 'Counting is finished. Check the discrepancies, then press “Close receiving”. Putting goods away into storage cells is in the “Putaway” block below.',
  'rcv.next.CLOSED': 'Receiving is closed. Nothing more to do here.',
  'rcv.next.CANCELLED': 'This request was cancelled.',
  'rcv.next.RETURN': 'Returns are received on the Returns page and on the handheld (Returns).',
  'rcv.tsd.EXPECTED': 'Or on the handheld: open the document, press “Mark arrived”, then “Start receiving”.',
  'rcv.tsd.ARRIVED': 'Or on the handheld: open the document and press “Start receiving”.',
  'rcv.tsd.RECEIVING': 'On the handheld: open the document, pick a line, scan the cell {locations} and then the item barcode.',
  'rcv.tsd.RECEIVED': 'On the handheld the document is listed as Received; it can be closed there too.',
  'rcv.act.START': 'Start receiving',
  'rcv.act.ARRIVE': 'Only mark as arrived',
  'rcv.act.FINISH': 'Finish receiving',
  'rcv.act.CLOSE': 'Close receiving',
  'rcv.done.START': 'Receiving started. Receive the lines below.',
  'rcv.done.ARRIVE': 'Marked as arrived.',
  'rcv.done.FINISH': 'Receiving finished.',
  'rcv.done.CLOSE': 'Receiving closed.',
  'rcv.blocked.noPermission': 'Your role cannot receive goods. Ask a warehouse manager.',
  'rcv.blocked.noZone': 'This warehouse has no receiving cell, so nothing can be received. Create the system cells in Settings → Warehouses first.',
  'rcv.blocked.openDiscrepancy': 'Cannot close yet: {count} line(s) with a discrepancy have no responsible party. Choose one in the “Responsibility” column.',
  'rcv.blocked.linesLocked': 'Lines can be received after “Start receiving”.',
  'rcv.warn.notAllReceived': '{count} line(s) are not fully received. You can still finish; the shortfall stays visible.',
  'rcv.colProduct': 'Product',
  'rcv.colProgress': 'Received / expected',
  'rcv.colReceivedBy': 'Received by',
  'rcv.colTime': 'When',
  'rcv.colWho': 'Who',
  'rcv.unidentified': 'Product not identified yet',
  'rcv.progress': '{received} of {expected}',
  'rcv.remaining': '{n} left to receive',
  'rcv.over': '{n} more than expected',
  'rcv.receive': 'Receive',
  'rcv.receiveLine': 'Receive this line',
  'rcv.qty': 'Quantity',
  'rcv.location': 'Where to receive',
  'rcv.discrepancy': 'Problem with the goods?',
  'rcv.party': 'Who is responsible?',
  'rcv.note': 'Note',
  'rcv.find': 'Find the product by code, name or barcode',
  'rcv.findHint': 'Needed when the product is unknown or differs from the document. Only this seller’s products are shown.',
  'rcv.pickedSku': 'Selected: {name}',
  'rcv.change': 'Change',
  'rcv.pick': 'Select',
  'rcv.noHits': 'Nothing found.',
  'rcv.submit': 'Receive',
  'rcv.cancel': 'Cancel',
  'rcv.assign': 'Save',
  'rcv.overage.state.PENDING': 'Surplus waiting for a decision',
  'rcv.overage.state.RESOLVED': 'Surplus resolved',
  'rcv.overage.state.REJECTED': 'Surplus rejected (needs separate handling)',
  'rcv.overage.state.DISPOSED': 'Surplus withdrawn',
  'rcv.overage.held': 'Held: cannot be put away or moved, not available for picking.',
  'rcv.overage.resolve': 'Accept surplus',
  'rcv.overage.reject': 'Reject',
  'rcv.overage.confirmResolve': 'Accept the surplus as ordinary stock? It becomes available for putaway.',
  'rcv.overage.dispose': 'Record withdrawal',
  'rcv.overage.disposeQty': 'Quantity withdrawn',
  'rcv.overage.disposeHow': 'How it was withdrawn',
  'rcv.overage.disposeReason': 'Reason',
  'rcv.overage.disposeNote': 'Note (write "returned to supplier" here for a return)',
  'rcv.overage.writeOff': 'Write-off',
  'rcv.overage.quarantine': 'Quarantine',
  'rcv.overage.confirmDispose': 'Confirm: {qty} unit(s) of the rejected surplus were physically withdrawn. The hold shrinks by exactly this quantity.',
  'rcv.overage.disposeSaved': 'Surplus withdrawal recorded.',
  'rcv.overage.saved': 'Surplus decision saved.',
  'rcv.assigned': 'Responsibility saved.',
  'rcv.done.line': 'Received {qty}. On this line: {received} of {expected}.',
  'rcv.err.qty': 'Enter a positive quantity (up to 4 decimals).',
  'rcv.err.sku': 'Select the product that actually arrived.',
  'rcv.err.wrongItem': 'This is not the product from the document. Choose “Wrong item” if another product really arrived.',
  'rcv.err.notReceiving': 'This request is not in receiving any more. Refresh the page.',
  'rcv.err.transition': 'This step is not possible in the current status. Refresh the page.',
  'rcv.err.openDiscrepancy': 'Cannot close: lines with a discrepancy still have no responsible party.',
  'rcv.err.shortage': 'Received less than announced: short by {short} on {count} line(s). Finish the receipt by the actual quantity? Missing units are not added to stock.',
  'rcv.err.overageUnresolved': 'Cannot close: lines with a surplus still need a decision (accept, reject or mark handled).',
  'rcv.err.noOverage': 'This line has no surplus: nothing to decide.',
  'rcv.err.disposalExceeds': 'That much is not held at this place: check the held quantity.',
  'rcv.err.disposalLocation': 'The surplus is held in several places: withdrawal is recorded per place (ask the system administrator).',
  'rcv.err.adjustBelowHeld': 'The correction would leave less than the held surplus. Decide the surplus and record its withdrawal first.',
  'rcv.err.overageHeld': 'Cannot put away: {held} unit(s) are held as an unresolved overage. Decide the surplus first (accept it or mark it handled).',
  'rcv.err.ownerMismatch': 'This product belongs to another seller.',
  'rcv.err.serial': 'Serial-tracked goods are received unit by unit on the handheld.',
  'rcv.err.returnKind': 'Returns are received on the Returns page.',
  'rcv.err.location': 'Choose the receiving zone of this warehouse.',
  'rcv.disc.NONE': 'No problem',
  'rcv.disc.SHORTAGE': 'Shortage',
  'rcv.disc.OVERAGE': 'Overage',
  'rcv.disc.WRONG_ITEM': 'Wrong item',
  'rcv.disc.DAMAGED': 'Damaged',
  'rcv.disc.NO_BARCODE': 'No barcode',
  'rcv.disc.EXPIRED': 'Expired',
  'rcv.party.UNDECIDED': 'Not decided',
  'rcv.party.WAREHOUSE': 'Warehouse',
  'rcv.party.OWNER': 'Seller',
  'rcv.party.CARRIER': 'Carrier',
  'rcv.party.CHANNEL': 'Channel',
  'rcv.history': 'Who received what',
  'rcv.history.empty': 'Nothing received yet.',
  'rcv.history.byDevice': '{name} (handheld)',
  'rcv.history.staff': 'Staff member',
  'rcv.activity': 'Actions in the office',
  'rcv.log.INBOUND_ARRIVED': 'Marked as arrived',
  'rcv.log.INBOUND_RECEIVING_STARTED': 'Started receiving',
  'rcv.log.INBOUND_RECEIVING_FINISHED': 'Finished receiving',
  'rcv.log.INBOUND_CLOSED': 'Closed receiving',
  'rcv.log.INBOUND_LINE_RECEIVED': 'Received {qty} on a line',
  'rcv.log.INBOUND_RESPONSIBILITY_ASSIGNED': 'Responsible party set: {party}',
  'rcv.log.other': 'Action',
  'rcv.log.INBOUND_PUTAWAY': 'Put away {qty} into cell {cell}',
  'rcv.flowNote': 'Goods are first received into the receiving zone. Once receiving is finished, put them away into storage cells.',
  'rcv.zone.RECEIVING': 'Receiving zone of “{warehouse}” ({code})',
  'rcv.zone.QUARANTINE': 'Quarantine — damaged or doubtful goods only ({code})',
  'rcv.noQuarantine': 'This warehouse has no quarantine zone — contact an administrator.',
  'put.title': 'Putaway into storage cells',
  'put.go': 'Go to putaway',
  'put.waiting': 'Waiting to be put away: {n} item(s) in the receiving zone {code} of “{warehouse}”.',
  'put.allDone': 'Nothing is left in the receiving zone — all goods are put away.',
  'put.tsd': 'On the handheld (TSD): Putaway → pick the goods from the “waiting to be put away” list (receiving zone {code}), scan the storage cell, then the product.',
  'put.tasks': 'Putaway tasks already open on handhelds: {n} ({qty} pcs to place).',
  'put.noReceivingZone': 'This warehouse has no receiving zone — contact an administrator.',
  'put.noPermission': 'Warehouse staff or an administrator can put goods away from the office. Everyone else uses the handheld.',
  'put.listLabel': 'Goods waiting to be put away',
  'put.available': 'Can be put away: {n} (zone {code})',
  'put.place': 'Put away',
  'put.panel': 'Put this stock away',
  'put.search': 'Find a cell by code or zone',
  'put.cell': 'Storage cells',
  'put.cellZone': 'zone {zone}',
  'put.cellEmpty': 'empty',
  'put.cellContent': 'Now: {n} product(s), {qty} pcs ({codes})',
  'put.noCells': 'This warehouse has no storage cells yet. Add them in',
  'put.noMatch': 'Nothing found.',
  'put.truncated': 'Showing the first {n}. Narrow the search.',
  'put.selected': 'Selected cell: {code}',
  'put.pickCell': 'Choose a storage cell.',
  'put.submit': 'Put away',
  'put.done': 'Put away {qty} into cell {cell}.',
  'put.err.qtyOver': 'You can put away at most {n}.',
  'put.err.destination': 'This cell cannot take goods: choose an active storage cell of this warehouse.',
  // Request revisions (ADR-089): the client edited a request after it was sent to the warehouse
  'revNotice.title': 'The client changed the request after it was sent to the warehouse (rev. {n}, {when})',
  'revNotice.hintInbound': 'Check the expected quantities before receiving. Goods already received are never changed by the client.',
  'revNotice.hintOrder': 'The reservation already follows the change. Check the lines before picking.',
  'revNotice.all': 'All revisions ({n})',
  'revNotice.rev': 'Revision {n}',
  'revNotice.submitted': 'Sent to the warehouse',
  'revNotice.mark': 'edited',
  'revNotice.markTitle': 'The client changed this request after it was sent',
  'revNotice.line.ADDED': '{sku}: added, quantity {to}',
  'revNotice.line.REMOVED': '{sku}: removed (was {from})',
  'revNotice.line.CHANGED': '{sku}: quantity {from} → {to}',
  'revNotice.fieldChange': '{field}: {from} → {to}',
  'revNotice.f.reference': 'Number',
  'revNotice.f.expectedAt': 'Expected date',
  'revNotice.f.dueAt': 'Date',
  'revNotice.f.shipTo.name': 'Recipient',
  'revNotice.f.shipTo.phone': 'Phone',
  'revNotice.f.shipTo.email': 'Email',
  'revNotice.f.shipTo.address': 'Address',
  'revNotice.f.shipTo.city': 'City',
  'revNotice.f.shipTo.postalCode': 'Postal code',
  'revNotice.f.shipTo.country': 'Country',
  'revNotice.f.shipTo.comment': 'Comment',
  // Scan & Receiving v2 (ADR-090): scanner mode, receiving by scan, move by scan
  'scan.mode': 'Scanner mode',
  'scan.mode.hint': 'Turn it on, then point the scanner at a barcode. It works like a keyboard, no need to click a field.',
  'scan.status.off': 'Scanner is off',
  'scan.status.waiting': 'Waiting for a scan',
  'scan.status.active': 'Scanner active',
  'scan.status.last': 'Last scan: {code}',
  'scan.field': 'Scan or type a code',
  'scan.field.placeholder': 'Code from the scanner or typed by hand',
  'scan.submit': 'Enter',
  'scan.manual': 'Type manually',
  'scan.manualOff': 'Hide the keyboard',
  'scan.duplicate': 'Repeated scan',
  'scan.duplicate.detail': 'The same code came again right away. It was counted once.',
  'scan.qty': 'Quantity per scan',
  'scan.qty.dec': 'Less',
  'scan.qty.inc': 'More',
  'scan.retry': 'Try again',
  'scan.result': 'Result of the last scan',
  'scan.ambiguous.title': 'This code is both a cell and a product. What is it?',
  'scan.ambiguous.location': 'A cell',
  'scan.ambiguous.product': 'A product',
  'scan.pickProduct': 'Several products have this barcode. Choose one.',
  'scan.pickOwner': 'Several sellers have this product in the cell. Choose whose it is.',
  'scan.err.unknown': 'Barcode not found',
  'scan.err.archived': 'The cell is archived',
  'scan.err.otherWarehouse': 'The cell belongs to another warehouse',
  'scan.err.notInRequest': 'This product is not in the request',
  'scan.err.needsReceiving': 'Damaged, expired, wrong or unmarked goods cannot go straight into a cell - receive them without placement',
  'scan.err.sourceNotAllowed': 'Goods cannot be taken from this place',
  'scan.err.destNotAllowed': 'Goods cannot be moved into this place',
  'scan.err.reserved': 'This stock is reserved for orders and cannot be moved',
  'scan.err.tracked': 'Goods with serial numbers or batches are handled one by one on the handheld',
  'scan.err.outOfScope': 'This warehouse is not available to your account',
  'scanRcv.title': 'Receiving by scanner',
  'scanRcv.request': 'Request {number} · {seller}',
  'scanRcv.mode.label': 'How to receive',
  'scanRcv.mode.TO_LOCATION': 'Receive straight into a cell',
  'scanRcv.mode.TO_RECEIVING': 'Receive without placement',
  'scanRcv.dest.receiving': 'Goods go to the receiving zone {code} (warehouse {warehouse})',
  'scanRcv.active.cell': 'ACTIVE CELL',
  'scanRcv.active.line': '{code} · received into it: {n} pcs',
  'scanRcv.needCell': 'Scan a cell first',
  'scanRcv.location.detected': 'Cell {code}',
  'scanRcv.location.detail': 'This is the active cell now. The next products go into it.',
  'scanRcv.location.hint': 'This is a cell. To receive straight into it, switch the mode.',
  'scanRcv.location.switch': 'Receive into cells',
  'scanRcv.product.detected': 'Product: {name}',
  'scanRcv.accepted': 'Received: {name} +{qty} → {cell}',
  'scanRcv.accepted.progress': 'On this line: {received} of {expected}.',
  'scanRcv.alreadyFull': 'This line was already received in full; the extra is counted as an overage.',
  'scanRcv.overage': 'More than the request announced.',
  'scanRcv.repeatHint': 'The same product was received twice in a row: check it is not a double scan.',
  'scanRcv.last': 'Last scanned:',
  'scanRcv.cells.title': 'Cells in this receiving',
  'scanRcv.cells.item': '{code}: {n} pcs',
  'scanRcv.progress': 'Received {received} of {expected}',
  'scanRcv.progress.label': 'Receiving progress',
  'scanRcv.col.expected': 'Expected',
  'scanRcv.col.accepted': 'Received',
  'scanRcv.col.disc': 'Discrepancy',
  'scanRcv.disc.btn': 'Discrepancy',
  'scanRcv.disc.title': 'Discrepancy for the next scan',
  'scanRcv.disc.none': 'No discrepancy',
  'scanRcv.disc.next': 'Applies to the next scanned product only.',
  'scanRcv.mode.needPutaway': 'Receiving into a cell needs the permission to put goods away.',
  'nav.moveScan': 'Move by scanner',
  'moveScan.title': 'Move by scanner',
  'moveScan.noAccess': 'Your role cannot move stock.',
  'moveScan.warehouse': 'Warehouse',
  'moveScan.warehouse.choose': 'Choose a warehouse',
  'moveScan.warehouse.need': 'Choose a warehouse to start.',
  'moveScan.steps': 'Steps',
  'moveScan.step.SOURCE': 'Source',
  'moveScan.step.DEST': 'Destination',
  'moveScan.step.ITEMS': 'Products',
  'moveScan.hint.SOURCE': 'Scan the cell to take goods from.',
  'moveScan.hint.DEST': 'Now scan the cell to move the goods to.',
  'moveScan.hint.ITEMS': 'Scan the products. Each scan moves the quantity set below.',
  'moveScan.source': 'From',
  'moveScan.dest': 'To',
  'moveScan.changeSource': 'Change source',
  'moveScan.changeDest': 'Change destination',
  'moveScan.quarantine': 'Quarantine',
  'moveScan.quarantine.hint': 'Turn on only to move goods into a quarantine cell.',
  'moveScan.sourceSet': 'Source: {code}',
  'moveScan.destSet': 'Destination: {code}',
  'moveScan.relocateHint': 'Cell {code} was not applied. To change a cell, press “Change source” or “Change destination”.',
  'moveScan.quarantineOff': 'Cell {code} is a quarantine cell. Turn on “Quarantine” to move goods there.',
  'moveScan.sameCell': 'The source and the destination must be different cells.',
  'moveScan.needSource': 'First scan the source cell.',
  'moveScan.needDest': 'First scan the destination cell.',
  'moveScan.moved': 'Moved: {name} {n} pcs from {from} to {to}',
  'moveScan.repeatHint': 'The same product was moved twice in a row: check it is not a double scan.',
  'moveScan.log.title': 'Last moves',
  'moveScan.log.empty': 'Nothing moved yet.',
  'moveScan.log.item': '{name} · {n} pcs · {from} → {to}',
  // Unified "Shipments" section (orders + their physical shipments in one working list and card)
  'shipments.tabsAria': 'Fulfilment type',
  'shipments.tabAll': 'All',
  'shipments.tabStandard': 'Standard',
  'shipments.tabFbs': 'FBS',
  'shipments.groupInWork': 'In work (picking, picked, packed)',
  'shipments.inWork': 'In work',
  'shipments.colOrder': 'Order #',
  'shipments.colTracking': 'Carrier / tracking',
  'shipments.colShipment': 'Shipment document',
  'shipments.viewDocuments': 'Physical shipment documents',
  'shipments.viewOrders': 'Back to shipments',
  'shipments.documentsTitle': 'Physical shipment documents',
  'shipments.sourceSeller': 'Seller portal',
  'shipments.card.recipient': 'Recipient',
  'shipments.card.noRecipient': 'No recipient recorded.',
  'shipments.card.physical': 'Physical shipments and tracking',
  'shipments.card.noPhysical': 'No physical shipment yet.',
  'shipments.card.openDocument': 'Open shipment document',
  'shipments.card.reservations': 'Reserved stock',
  'shipments.card.noReservations': 'Nothing reserved for this order.',
  'shipments.card.tasks': 'Pick tasks',
  'shipments.card.noTasks': 'No pick tasks yet.',
  'shipments.card.colCell': 'Cell',
  'shipments.card.colQty': 'Qty',
  'shipments.card.colDone': 'Done / target',
  'shipments.card.legacyLooking': 'Opening the shipment…',
  'shipments.card.legacyFailed': 'This shipment could not be opened.',
  // Ops: create a supply (inbound) for a seller
  'inbounds.newInbound': 'New supply',
  'inboundCreate.title': 'New supply',
  'inboundCreate.subtitle': 'Announce a supply for a seller. The document number is issued by the system; the seller sees it in their portal.',
  'inboundCreate.permissionDenied': 'Creating a supply needs the receiving permission.',
  'inboundCreate.seller': 'Seller',
  'inboundCreate.selectSeller': 'Choose a seller',
  'inboundCreate.warehouse': 'Warehouse',
  'inboundCreate.selectWarehouse': 'Choose a warehouse',
  'inboundCreate.sellerChangeResets': 'Changing the seller clears the items below.',
  'inboundCreate.chooseSellerFirst': 'Choose a seller and a warehouse first.',
  'inboundCreate.findItem': 'Find an item (code, name, article or barcode)',
  'inboundCreate.itemResults': 'Items of this seller',
  'inboundCreate.noItemsFound': 'No items found for this seller.',
  'inboundCreate.addItem': 'Add {sku}',
  'inboundCreate.lines': 'Items to receive',
  'inboundCreate.noLines': 'No items added yet.',
  'inboundCreate.lineMerged': '{sku} is already in the list: quantity increased.',
  'inboundCreate.colItem': 'Item',
  'inboundCreate.colQty': 'Planned qty',
  'inboundCreate.qtyFor': 'Planned quantity for {sku}',
  'inboundCreate.qtyInvalid': 'Enter a whole quantity above zero for {sku}.',
  'inboundCreate.remove': 'Remove',
  'inboundCreate.removeLine': 'Remove {sku}',
  'inboundCreate.details': 'Details',
  'inboundCreate.reference': 'Seller reference (optional)',
  'inboundCreate.referenceHint': 'Letters, digits and dashes. If empty, the system issues the number by its standard scheme.',
  'inboundCreate.expected': 'Expected date (optional)',
  'inboundCreate.fixErrors': 'Fix the highlighted quantities first.',
  'inboundCreate.submit': 'Create supply',
  'inboundCreate.submitting': 'Creating…',
  'inboundCreate.success': 'Supply {number} created',
  'inboundCreate.open': 'Open supply {number}',
  'inboundCreate.another': 'Create another',
  'inboundCreate.err.ownerNotAllowed': 'This seller cannot be used here (not found or archived).',
  'inboundCreate.err.warehouseNotAllowed': 'This warehouse does not serve this seller: there is no active contract with it.',
  'inboundCreate.err.contractNotActive': 'The seller has no active contract.',
  'inboundCreate.err.skuNotOwned': 'The item {sku} does not belong to this seller.',
  'inboundCreate.err.linesRequired': 'Add at least one item.',
  'inboundCreate.err.referenceInvalid': 'The reference may only contain letters, digits and dashes.',
  'inboundCreate.err.numberTaken': 'This seller already has a supply with this reference. Use another one or leave it empty.',
  'inboundCreate.err.notFound': 'The seller or warehouse no longer exists, or is outside your access. Reload the page and choose again.',
  'shipments.tabUnknown': 'Not classified',
  'shipments.colModel': 'Fulfilment',
  'fulfilment.STANDARD': 'Standard',
  'fulfilment.FBS': 'FBS',
  'fulfilment.UNKNOWN': 'Not classified',
  'shipments.card.fulfilment': 'Fulfilment type',
  'shipments.card.unknownHint': 'This order has no fulfilment type. Old orders are not classified automatically: choose the right one.',
  'shipments.card.setStandard': 'Mark as Standard',
  'shipments.card.setFbs': 'Mark as FBS',
  'shipments.card.decideConfirm': 'Set "{model}" for order {number}? It cannot be changed here afterwards.',
  'shipments.card.decided': 'Fulfilment type saved.',
  'shipments.card.alreadySet': 'This order was already classified. Reload the page.',
  'orderCreate.fulfilment': 'Fulfilment type',
  'orderCreate.fulfilmentSelect': 'Choose…',
  'orderCreate.fulfilmentStandard': 'Standard (the warehouse ships to the recipient)',
  'orderCreate.fulfilmentFbs': 'FBS (a marketplace order fulfilled by the warehouse)',
  'orderCreate.fulfilmentHint': 'Required. It is not taken from the channel; it decides which tab of Shipments the order is in.',
  'inboundCreate.warehouseReason.NO_WAREHOUSE': 'No warehouse is assigned to this seller yet.',
  'inboundCreate.warehouseReason.CONTRACT_NOT_ACTIVE': 'This seller has no active contract, so no warehouse can receive their supplies.',
  'inboundCreate.warehouseReason.OUT_OF_SCOPE': 'This seller is not served by your warehouse.',
  'inboundCreate.warehouseOnly': 'Warehouses are limited to those serving this seller under an active contract.',
  'shipments.bulk.hint': 'Select the old orders you are sure about. Nothing is classified automatically.',
  'shipments.bulk.select': 'Select',
  'shipments.bulk.selectAria': 'Select order {number}',
  'shipments.bulk.selectAll': 'Select all on this page',
  'shipments.bulk.count': '{n} selected',
  'shipments.bulk.setStandard': 'Mark selected as Standard',
  'shipments.bulk.setFbs': 'Mark selected as FBS',
  'shipments.bulk.confirm': 'Set "{model}" for {n} selected orders? Orders that are already classified are never changed, and nothing else about these orders changes.',
  'shipments.bulk.done': 'Classified: {updated}. Already had this type: {unchanged}.',
  'shipments.bulk.conflict': 'Some of the selected orders were already classified differently. Nothing was changed. Reload the list.',
  'shipments.bulk.notFound': 'Some of the selected orders are no longer available to you. Nothing was changed. Reload the list.',
  'nav.putaway': 'Putaway',
  'inbounds.colAwaitingPutaway': 'Awaiting putaway',
  'putaway.awaitingBadge': 'Awaiting putaway: {n}',
  'putaway.awaitingHint': 'Received goods that are still in the receiving zone of the warehouse and are not available for picking until they are placed (shared pool of the product).',
  'putaway.title': 'Putaway',
  'putaway.subtitle': 'Received goods wait in the receiving zone and cannot be picked. They become available only after a confirmed scanned move into a storage cell.',
  'putaway.plan': 'Create tasks',
  'putaway.planned': 'Tasks created: {n}, units: {qty}.',
  'putaway.statUnplanned': 'Awaiting a task',
  'putaway.statTasks': 'Open tasks',
  'putaway.statInWork': 'In work',
  'putaway.statHeld': 'Held surplus (waits for a decision)',
  'putaway.awaitingTitle': 'Awaiting putaway',
  'putaway.awaitingEmpty': 'Nothing is waiting in the receiving zone.',
  'putaway.tasksTitle': 'Putaway tasks',
  'putaway.tasksEmpty': 'No open putaway tasks.',
  'putaway.colCell': 'Receiving cell',
  'putaway.colOnHand': 'In zone',
  'putaway.colHeld': 'Held',
  'putaway.colPlanned': 'In tasks',
  'putaway.colUnplanned': 'Not planned',
  'putaway.colSince': 'Since',
  'putaway.colDone': 'Placed / planned',
  'putaway.colHolder': 'Held by',
  'putaway.colCreated': 'Created',
  'putaway.serial': 'serial',
  'putaway.serialHint': 'placed unit by unit on the handheld',
  'putaway.waitsForReturn': 'waits for the return decision',
  'putaway.heldHint': 'An unresolved receiving surplus: not offered for putaway until the office decides.',
  'putaway.leaseLapsed': 'lease expired',
  'putaway.release': 'Release',
  'putaway.cancelTask': 'Withdraw',
  'putaway.confirmAria': 'Confirm the task action',
  'putaway.confirmRelease': 'Free this task so another device can take it?',
  'putaway.confirmCancel': 'Withdraw this task? Nothing is moved; the units wait for a new task.',
  'putaway.released': 'The task is free again.',
  'putaway.cancelled': 'The task was withdrawn.',
} as const;

export type TranslationKey = keyof typeof en;
